define("ShoppingCart", [`Model`, `module`, `lodash`], function (Super, Self, _) {
	const DUMMY_RECAPTCHA = `dummytext`;
	const STEPS = {
		// the potentially required steps of checkout
		token: true,
		add_ons: false,
		q_config: false,
		logged_in: false,
		required_forms: false,
		shipping_address: false, // whether a shipping address is required to submit the order
		payment_plan: false, // used for "force payment plan"
		order_protection: false, // whether opting in or out of insurance is required
		payment: false, // whether a payment method is required to submit the order
		address_match: false, // whether the billing and shipping addresses must match
		optional_forms: false,
	};
	const DEFAULT_CART = {
		// the default 'starting' cart; it will also be user to overwrite existing values when the cart is 'reset'
		agentCheckoutPaymentOptions: null, // to be populated by the "reserve shopping cart" response
		checkoutMode: null, // AGENT | GUEST | USER
		checkoutType: null, // PURCHASE | UPGRADE
		completed: false, // whether the purchase has been completed
		features: {
			payment_plan: false, // enabling payment plan is allowed
			promo_code: false, // entering a promo is allowed
			will_call_substitution: false, // allow substituting 'will call' for international shipping orders
			payment_plan_optional: false, // used in cases where only a URL param is forcing PP
		},
		copy: {
			add_on_blurb: null,
			order_complete_blurb: null,
		},
		requirements: _.cloneDeep(STEPS), // a list of potential 'requirements' the user must complete to purchase
		progress: _.cloneDeep(STEPS), // a map of user progress, to compare to the requirements map
		eventId: null, // the associated event id
		currency: null, // to be populated based on the initiated event
		saleItems: [], // to be populated by the "reserve shopping cart" response
		sales: [], // to be populated with the list of available sales
		holds: [], // to be populated with the list of available holds
		products: [], // the full list of visible products
		subtotal: null, // the locally calculated sum of item prices
		total: null, // the total cost of the cart
		orderProtection: null, // cost of optional order protection (if opted-in)
		unlockedHolds: [], // updated when a user applies access codes
		saleUnlocks: [], // updated when a user applies access codes
		tenderTypeOptions: [`CREDIT_CARD`],
		tenderType: `NONE`, // NONE | CREDIT_CARD
		token: null, // the unique token provided by the server
		authenticated: null, // used to double-check the user is logged in when proceeding to checkout
		expiration: null, // a timestamp of when the cart expires
		queue_settings: null, // 	s any queue details, if the tickets are sold out
		promoCode: null, // an optional promo code applied to the order
		paymentSchedule: null, // a breakdown of what the user pays if using a payment plan
		usePaymentPlan: false, // whether the user has enabled payment plan
		paymentPlanFee: null, // an additional payment plan fee; to be displayed on checkout
		paymentPlanTotal: null, // the value to use for order total, if payment plan is enabled
		deliveryFee: null, // an additional delivery fee; to be displayed on checkout
		feesAndTaxes: null, // a value that's used for when all-in pricing is disabled
		fee_and_tax_breakdown: null, // a value that's used for when all-in pricing is disabled
		discount: null, // a discount applied to the current cart; displayed in checkout summary
		nonce: null, // used for submitting a new payment method
		paymentMethodToken: null, // used for submitting an existing payment method
		billingAddress: null, // used to hold the address for authorizing the payment
		shippingAddress: null, // used to hold a shipping address, if required
		savePaymentMethod: true, // whether to save a new payment method
		useAsDefault: true, // whether to use a new payment method as the new default
		orderId: null, // holds the returned orderId of a successful submission
		blurb: null, // used to hold any special blurb text, to be presented to the user on order completion
		additionalFees: null, // used to hold any additional ees to display to the user
		checkoutLabel: null, // used to dictate the label to show on the checkout and scroll-to buttons
		quantity: 0, // a quick reference to the current total cart quantity
		eventCategoryName: null, // pass-through from the event object
		privateLinkToken: null, // whether a plt was passed in the URL (single-use token to buy a private sale)
		paymentProcessorType: `STRIPE`,
		paymentIntent: null,
		requireSavedPayment: false,
		violations: [], // used for keeping track of broken rules when selecting seats
		maxTicketsAllowed: null, // used for tracking the overall max number of tickets the user can buy
		guestCheckoutBehavior: null, // ALL | AGENT_ONLY | GUEST | NONE
		guestCheckoutDetails: null, // an optional ability to pre-populate and lock guest email
		orderProtectionConfiguration: null, // optional order protection config
		orderProtectionAdded: null, // null | true | false
		upgradeOrderProtectionFee: null, // required fee when upgrading protected order
		upgradeOrderProtectionItemCosts: null,
	};
	const upgradesFindBestBlockOptions = { key: `upgradesFindBest`, message: `Finding Best Available` };
	const stateOverrides = {};
	let buyRequestPlaced = false;
	let setUserPromise = null;
	let expirationTimerId;
	let cartDeferred;

	return Super.inherit({
		namespace: Self.id,
		singleton: true, // utilty class, only one instance
		requires: [
			`Countdown`,
			`DOM`,
			`Events`,
			`Insights`,
			`Locale`,
			`Orders`,
			`Payment`,
			`ReservedSeating`,
			`Storage`,
			`System`,
			`Time`,
			`Templates`,
			`User`,
		],
		constructor(setup) {
			const { App, Config, Locale, Storage, Time, User, data, promises } = this;
			const agentHandle = App.getCookie(`agentHandle`);

			this.assign(data, {
				agentHandle,
				cart: null, // keeps track of the cart state
				checkoutActive: false,
				checkoutMode: `USER`,
				custom_forms: [],
				enablePersistentCheckout: true,
				event: null, // cached reference to the specified event
				ignoreConsecutiveSeatingViolations: false, // used for cases where F.B.A. has returned split seats
				incomplete_promo: null,
			});

			Locale.get({
				fireImmediately: true, // fire the below handler immedidately
				subscribe: (localeInfo) => {
					// keep track of the locale
					data.selectedLocale = localeInfo.selected;
					this.updateLocaleSetting();
				},
			});

			cartDeferred = this.Deferred();
			const { promise } = cartDeferred;
			promises.cart = promise;

			this.when({
				promise,
				then: () => {
					// cart is ready, listen for messages from Studio (used for agent checkout)
					const { ENVIRONMENT } = Config;
					const isLocal = ENVIRONMENT === `dev`;
					const { location } = window;
					const { origin: siteOrigin } = location;
					const { cart } = data;

					window.addEventListener(
						`message`,
						({ data: evt, origin }) => {
							const { action, value } = evt;

							if (!isLocal) {
								const tixrDomain = origin.endsWith(`tixr.com`);
								const studioDomain = origin.indexOf(`studio`) > -1;

								if (!(tixrDomain && studioDomain)) {
									return; // not from Studio, ignore
								}
							} else if (origin === siteOrigin) {
								return; // not from Studio, ignore
							}

							// messages from Box Office opening this page via iframe
							switch (action) {
								case `clear-guest-account`:
									cart.guestCheckoutDetails = null;
									this.reconcile();
									break;

								case `close-agent-checkout`:
									App.deleteCookie(`agentHandle`);
									this.init();
									break;

								case `set-guest-account`: {
									const { email, firstName, lastName, id } = value;
									cart.guestCheckoutDetails = { email, firstName, lastName, id };
									this.reconcile();
									break;
								}
							}
						},
						false
					);

					const { parent } = window;
					if (this.isObject(parent) && this.isFunction(parent.postMessage)) {
						// try to notify parent Studio frame that cart is ready
						try {
							const evt = { action: `shopping-cart-ready` };
							const targetOrigin = `*`; // TODO: introduce Studio domain variable
							parent.postMessage(evt, targetOrigin);
						} catch (err) {
							this.log(`Agent Checkout: Error communicating with parent frame.`);
						}
					}
				},
			});

			const cartToken = Storage.get({ key: `cart_token` });
			const cartExpirationString = Storage.get({ key: `cart_expiration` });
			const orderTokenParam = App.getUrlParam(`stripe_redirect_token`); // Event.js uses this for order completion on stripe redirect

			if (!this.isString(orderTokenParam, 1) && this.isString(cartToken, 1) && this.isString(cartExpirationString, 1)) {
				const cartExpiration = this.parseInt(cartExpirationString);
				const currentTime = Time.getTime();

				if (cartExpiration > currentTime) {
					data.initialCartToken = cartToken;
					data.initialCartExpiration = cartExpiration;
				}
			}

			return User.get({
				subscribe: true,
				then: (user) => {
					const { cart, event } = data;

					data.user = user;

					if (this.isObject(cart) && this.isString(cart.token, 1) && !cart.completed) {
						const { requiredAgeRestriction } = event;
						let initializePayment = true;

						// check for an age restriction
						if (this.isInt(requiredAgeRestriction, 1)) {
							const { birthday } = user;

							if (this.isArray(birthday) && birthday.length === 3) {
								const date = new Date();
								const year = date.getFullYear() - requiredAgeRestriction;
								const month = date.getMonth();
								const day = date.getDate();
								const maxDate = new Date(year, month, day);
								const birthDate = new Date(birthday[0], birthday[1] - 1, birthday[2]);
								const maxDateString = Time.format(`%Y-%M-%D`, maxDate.getTime());
								const birthDateString = Time.format(`%Y-%M-%D`, birthDate.getTime());

								if (maxDateString < birthDateString) {
									initializePayment = false;
								}
							} else {
								initializePayment = false;
							}
						}

						if (user.paymentIntent) {
							cart.paymentIntent = user.paymentIntent;
						}

						if (initializePayment) {
							// active checkout
							const { paymentIntent, paymentProcessorType, requirements } = cart;

							if (requirements.payment && paymentProcessorType === `STRIPE` && !this.isObject(paymentIntent)) {
								return this.getPaymentIntent(); // NOTE: eventually calls reconcile(), return and skip below
							}
						}
					}

					this.reconcile();
				},
				fail: () => {
					data.user = null;
					this.reconcile();
				},
			});
		},

		api: {
			init: {
				async: true,
				clone: true,
				public: true,
				defaults: {
					eventId: null,
					clear: true, // whether to remove all items from the cart (e.g. true on completed checkout)
					confirm: false, // whether to ask the user before proceeding
					isUpgrades: false, // whether the cart is being initialized for upgrades
					resumeCheckout: false, // whether to try resuming if a cart token is present
					skipDelete: false, // whether to skip the DELETE call when voiding an existing cart,
					groupSaleId: null, // id of the group sale
					groupSaleUrlParam: null, // url to return user to when exiting group sale flow
				},
				async value(context, options) {
					const { App, Countdown, Events, Payment, ReservedSeating, Storage, User, XHR, data } = this;
					const { agentHandle } = data;
					const { confirm, isUpgrades, resumeCheckout } = options;
					const checkoutType = isUpgrades ? `UPGRADE` : `PURCHASE`;
					const purchaseFlow = isUpgrades ? `Upgrade` : `Purchase`;
					let { cart } = data;
					let hasToken = this.isObject(cart) && this.isString(cart.token);
					let eventId;

					if (hasToken && resumeCheckout) {
						// for cases where the cart is already populated & active
						// e.g. navigating away from and returning to the event page
						const { completed, eventId, saleItems } = cart;
						const hasMatchingEvent = eventId === options.eventId; // same event as active checkout
						const hasItems = this.isArray(saleItems, 1); // items from the server in cart

						if (hasMatchingEvent && hasItems) {
							if (completed) {
								// cart is active but completed, show the order complete overlay
								App.loadOverlay({
									type: `OrderComplete`,
									setup: { theme: `LIGHT`, overlayMode: `modal`, style: `ux4` },
									replace: true,
								});
							} else {
								this.checkout();
							}

							return this.clone(cart);
						}
					}

					data.isUpgrades = isUpgrades;
					data.skipOrderProtection = false; // used to keep track of widget failure
					data.enablePersistentCheckout = true; // reset to default
					data.ignoreConsecutiveSeatingViolations = false; // reset to default
					data.purchaseFlow = purchaseFlow;
					data.groupSaleId = options.groupSaleId;
					data.groupSaleUrlParam = options.groupSaleUrlParam;

					if (confirm && hasToken) {
						const message = `Exiting checkout will result in your shopping cart being cleared. Do you want to proceed?`;
						const confirmExit = await App.askUser({ message });

						if (!confirmExit) {
							return; // user cancelled, skip the rest of the function
						}
					}

					Countdown.clearExpiration();

					const [OrderProtection] = await this.require({ deps: [`OrderProtection`] });
					OrderProtection.clearSession();

					// clear existing stripe elment information if it exists
					Payment.clearStripeElementsInfo();

					// reset any existing timer
					this.setExpiration(null);

					if (this.isInt(options.eventId)) {
						// event id was manually passed
						eventId = options.eventId;
					}

					if (this.isObject(cart)) {
						// there's an existing cart
						if (this.isUndefined(eventId) && this.isInt(cart.eventId)) {
							eventId = cart.eventId;
						}

						if (this.isString(cart.token, 1) && !cart.completed) {
							// current cart is incomplete

							if (!options.skipDelete) {
								// NOTE: don't thread into the promise chain, assume success
								XHR.ajax({
									// void the existing token
									url: `/api/events/${cart.eventId}/shoppingcarts/${cart.token}`,
									ignoreErrors: true,
									type: `DELETE`,
									block: false,
								});
							}

							this.leaveUpgradesMobileWebview();

							// NOTE: DELETE call above invalidates reserved seating session
							ReservedSeating.endSession();
						} else {
							// NOTE: don't thread into the promise chain, assume success
							ReservedSeating.endSession();
						}

						if (cart.checkoutMode !== `USER`) {
							// guest or agent checkout

							if (this.isObject(data.user)) {
								User.logOut();
							}
						}
					}

					if (this.isUndefined(eventId)) {
						throw new Error(`Missing event id`);
					}

					const event = await Events.get({
						by: `id`,
						id: eventId,
						force: isUpgrades,
						params: isUpgrades ? { mode: `UPGRADE` } : undefined, // mode=UPGRADE returns add-ons with sales
					});

					let preservedProducts = []; // will contain values to carry over after cloning DEFAULT_CART
					let saleUnlocks = [];
					let unlockedHolds = [];
					let privateLinkToken;
					let groupSaleToken;

					if (this.isObject(cart)) {
						const includedProducts = this.filter(cart.products, ({ quantity }) => quantity > 0);
						const reservedProduct = this.find(includedProducts, ({ isReserved }) => isReserved);

						if (this.isObject(reservedProduct)) {
							// a 'reserved' sale is in the cart
							// they'll be removed, clear all for consistent behavior
							options.clear = true;
						}

						// a previous cart exists
						if (!options.clear && cart.eventId === eventId) {
							// preserve some aspects
							preservedProducts = this.map(includedProducts, (product) => {
								const { id, productType, selections } = product;
								return { id, productType, selections };
							});

							saleUnlocks = cart.saleUnlocks;
							unlockedHolds = cart.unlockedHolds;
						}

						if (this.isString(cart.privateLinkToken, 1)) {
							// a token exists
							if (cart.eventId === eventId) {
								// the same event is being initialized
								privateLinkToken = cart.privateLinkToken;
							}
						}

						if (this.isString(cart.groupSaleToken, 1)) {
							// a token exists
							if (cart.eventId === eventId) {
								// the same event is being initialized
								groupSaleToken = cart.groupSaleToken;
							}
						}
					}

					if (this.isString(options.privateLinkToken, 1)) {
						// a token was passed, use it (overrides existing if present)
						privateLinkToken = options.privateLinkToken;
					}

					if (this.isString(options.groupSaleToken, 1)) {
						// a token was passed, use it (overrides existing if present)
						groupSaleToken = options.groupSaleToken;
					}

					cart = this.clone(DEFAULT_CART);

					cart.eventId = event.id;
					cart.unlockedHolds = unlockedHolds;
					cart.saleUnlocks = saleUnlocks;
					cart.currency = event.currency;
					cart.checkoutType = checkoutType;

					if (this.isString(privateLinkToken, 1)) {
						cart.privateLinkToken = privateLinkToken;
					}

					if (this.isString(groupSaleToken, 1)) {
						cart.groupSaleToken = groupSaleToken;
					}

					data.custom_forms = [];
					data.incomplete_promo = null;

					data.checkoutMode = this.isString(agentHandle, 1) ? `AGENT` : `USER`;

					data.event = event;
					data.cart = cart;

					cart.paymentProcessorType = event.paymentProcessorType;
					cart.features.will_call_substitution = event.shippingSetting.willCallForUnsupported;

					cart.requirements.address_match = event.shippingSetting.billingShippingMatch;

					if (this.isObject(event.queue_settings)) {
						data.queueBehavior = event.queue_settings.behavior;
					} else {
						data.queueBehavior = false;
					}

					cart.eventCategoryName = event.eventCategoryName;
					cart.filterConfiguration = this.clone(event.filterConfiguration);
					cart.sales = this.clone(event.sales);
					cart.holds = this.clone(event.holds);

					this.chain(cart.sales)
						.concat(cart.holds)
						.clone()
						.each(this.initProduct)
						.sortBy((product) => product.ordinal)
						.tap((products) => (cart.products = products));

					cart.maxTicketsAllowed = event.maxTicketsAllowed; // NOTE: can be null

					cartDeferred.resolve(cart); // make sure the initial promise is resolved
					this.promises.cart = this.when({
						// keep the promise up-to-date with the current
						promise: cart,
					});

					await this.updateProducts({ products: preservedProducts });

					const { initialCartToken } = data;

					if (options.clear) {
						Storage.remove({ key: `cart_token` });
						Storage.remove({ key: `cart_expiration` });
						// clear the initial token
						data.initialCartToken = null;
					} else if (this.isString(initialCartToken, 1)) {
						try {
							// fetch the cart session from the server
							const response = await this.fetch({ eventId: options.eventId, token: initialCartToken });

							data.initialCartToken = null;

							// sync the cart with the server response
							await this.syncCart({ response });

							if (this.isArray(data.cart.saleItems, 1) && !data.cart.completed) {
								return this.checkout();
							}
						} catch (err) {
							/* swallow any errors */
						}
					}

					this.reconcile();

					if (this.isString(options.accessCode, 1)) {
						const accessCode = options.accessCode.toUpperCase();
						this.addAccessCode({ accessCode });

						if (this.isString(options.groupSaleId)) {
							data.groupSaleAccessCode = accessCode;
						}
					}

					return this.clone(cart);
				},
			},

			clearContents: {
				async: true,
				public: true,
				defaults: {
					confirm: false,
					message: null,
				},
				async value(context, options) {
					const { App, data } = this;
					const { cart } = data;
					const { confirm, message: optionsMessage } = options;

					if (confirm) {
						const { quantity: cartQuantity } = cart;

						if (cartQuantity > 0) {
							// There are tickets present in the standard cart
							const defaultMessage = `Would you like to remove your carted tickets and continue?`;
							const message = optionsMessage === null ? defaultMessage : optionsMessage;

							const response = await App.askUser({ message });

							if (!response) {
								return false;
							}
						}
					}

					const products = this.map(cart.products, (product) => {
						const { id, productType } = product;
						return { id, productType, selections: [] };
					});

					this.updateProducts({ products });
					return true;
				},
			},

			initProduct: {
				public: false,
				value(product) {
					const { Utils, data } = this;
					const { agentHandle } = data;

					if (this.isString(agentHandle, 1) && product.state === `RESALE`) {
						// agent checkout is not supported for agents
						product.state = `SOLD_OUT`;
					}

					const { id, productType, state } = product;
					const displayState = state; // overrides from the server, e.g. access code unlocks
					const localState = state; // overrides from user history / behavior, e.g. AT_LIMIT
					const selections = []; // used to track seat and price selection choices

					// if (productType === `HOLD`) {
					// 	// NOTE: regardless of how it made it into cart.products, should leave clues to represent
					// 	// being unlocked. e.g. CollectionPicker can tell if a thumbnail has unlocked products
					// 	product.state = `LOCKED`;
					// }

					if (product.isAddOn && this.isObject(data.cart.addOnPacks)) {
						// indicate which add-on packs the add-on belongs to
						product.addOnPackIndexes = this.map(data.cart.addOnPacks, ({ addOns }, index) =>
							addOns.find(({ id }) => id === product.id) ? index : null
						).filter((index) => !this.isNull(index));
					}

					// default ignorePurchasedQuantity to false
					if (product.ignorePurchasedQuantity !== true) {
						product.ignorePurchasedQuantity = false;
					}

					if (state === `OPEN` && productType === `SALE`) {
						// possible to add to cart, holds not supported
						const { isLevels, priceLevels, tiers } = product;
						const { id: tierId } = tiers[0];

						if (isLevels) {
							// price levels are enabled, check level for minBehavior
							this.each(priceLevels, (priceLevel) => {
								const { initialQuantity } = priceLevel;

								if (initialQuantity > 0) {
									// price level should be added to cart
									const indices = Utils.range(0, initialQuantity);
									const { value: priceLevelOptionId } = priceLevel;

									this.each(indices, () => selections.push({ priceLevelOptionId, saleId: id, tierId }));
								}
							});
						} else {
							// standard pricing, check sale for minBehavior
							const { initialQuantity } = product;

							if (product.initialQuantity > 0) {
								// product should be added to cart
								const indices = Utils.range(0, initialQuantity);

								this.each(indices, () => selections.push({ saleId: id, tierId }));
							}
						}
					}

					this.assign(product, { displayState, localState, quantity: 0, selections: [] });

					// a default quantity is being inserted, defer before doing that
					if (selections.length > 0) {
						this.defer({
							then: () =>
								this.updateProducts({
									products: [{ id, productType, selections }],
								}),
						});
					}

					return product;
				},
			},

			setCheckoutMode: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { mode } = options;

					if (data.checkoutMode !== mode) {
						data.checkoutMode = mode;
						this.reconcile();
					}
				},
			},

			setExpiration: {
				value(expiration) {
					const { Countdown, ReservedSeating, Time, data } = this;
					const { cart } = data;
					const currentTime = Time.getTime(); // use the server-adjusted timestamp
					let timerId = null;

					window.clearTimeout(expirationTimerId);

					if (this.isInt(expiration)) {
						const timeLeft = expiration - currentTime;
						timerId = window.setTimeout(this.expireCart, timeLeft);
						ReservedSeating.setExpiration(expiration);
						Countdown.setExpiration(expiration);
					}

					expirationTimerId = timerId;

					if (this.isObject(cart)) {
						cart.expiration = expiration;
					}
				},
			},

			expireCart: {
				public: true,
				value(context, options) {
					const { App } = this;
					const expiration = null;

					// reset the ttimer
					this.setExpiration(expiration);

					if (!buyRequestPlaced) {
						// don't reset if purchase is already in process
						this.init();

						App.notifyUser({
							type: `error`,
							message: `Sorry, your ticket reservation has expired, please try again`,
						});
					} // keep holding out if the request has been placed
				},
			},

			syncCart: {
				async: true,
				value(options) {
					// ingests the different server responses
					const { App, Config, Events, Templates, Utils, XHR, Storage, data } = this;
					const { response } = options;
					const { custom_form_assignments, expiration, saleItems } = response;
					const { cart, event } = data;
					const urlParams = App.getQueryParams();
					const keys = [
						`add_ons_blurb`,
						`add_ons`,
						`addOnPacks`,
						`additionalFees`,
						`agentCheckoutFanId`,
						`agentCheckoutPaymentOptions`,
						`blurb`,
						`checkoutConfig`,
						`currentPayment`,
						`customPaymentSchedule`,
						`discount`,
						`fee_and_tax_breakdown`,
						`feesAndTaxes`,
						`groupSaleToken`,
						`guestCheckoutBehavior`,
						`hasBatchSubmissions`,
						`hasShippingSaleItems`,
						`paymentIntent`,
						`paymentPlanFee`,
						`paymentPlanTotal`,
						`paymentSchedule`,
						`privateLinkToken`,
						`promoCode`,
						`queue_settings`,
						`shipping`,
						`showAddOns`,
						`showWaitlist`,
						`token`,
						`total`,
						`upgradeInfo`,
						`upgradeSubTotal`,
						`usePaymentPlan`,
						`violations`, // used locally for keeping track of seats.io violations
						`orderProtection`,
						`orderProtectionConfiguration`,
						`upgradeOrderProtectionFee`,
						`upgradeOrderProtectionItemCosts`,
						`useCustomPaymentSchedule`,
						`totalDueNow`,
						`skipUserInfo`,
						`useSalesInvoice`,
					];
					let q_required;
					let promise;

					this.each(keys, (key) => {
						if (!this.isUndefined(response[key])) {
							cart[key] = response[key];
						}
					});

					cart.deliveryFee = this.isFinite(response.deliveryFee) ? response.deliveryFee : null;

					if (this.isInt(expiration)) {
						this.setExpiration(expiration);
					}

					const {
						features,
						orderProtection,
						orderProtectionConfiguration,
						progress,
						requirements,
						upgradeOrderProtectionFee,
					} = cart;

					q_required = this.isObject(cart.queue_settings);

					if (this.isFinite(cart.total)) {
						requirements.payment = cart.total > 0;
						cart.tenderType = `NONE`;
					}

					if (!this.isObject(response.paymentSchedule)) {
						if (cart.usePaymentPlan) {
							App.notifyUser({
								type: `warning`,
								message: `Order total no longer meets the minimum amount required for payment plan use.`,
							});
						}

						cart.usePaymentPlan = false;

						if (this.isObject(cart.paymentSchedule)) {
							cart.paymentSchedule = null; // reset any previously stored schedule
						}
					}

					if (this.isFinite(upgradeOrderProtectionFee) && upgradeOrderProtectionFee !== 0) {
						// upgrading an order that was protected, fee is required and listed already
						requirements.order_protection = false;
					} else if (requirements.payment) {
						// requires payment
						const { skipOrderProtection } = data;
						const available = this.isObject(orderProtectionConfiguration) && orderProtectionConfiguration.enabled;

						if (available && !skipOrderProtection) {
							// order protection plan is available and enabled
							requirements.order_protection = true;
						} else {
							// no order protection plan available
							requirements.order_protection = false;
						}
					} else {
						// e.g. free order
						requirements.order_protection = false;
						progress.order_protection = true; // important, so resetting to false via back button counts as a change
						cart.usePaymentPlan = false;

						if (this.isObject(orderProtection)) {
							// order protection was previously added, but not compatible with free orders
							// NOTE: return, since this function will be called again after removing order protection
							return this.removeOrderProtection();
						}
					}

					if (cart.hasShippingSaleItems === true) {
						requirements.shipping_address = true;
					} else if (cart.hasShippingSaleItems === false) {
						requirements.shipping_address = false;
					}

					this.each(saleItems, (item) => {
						// preserve any existing properties not included in the response
						const { conflicts, holdId, saleId } = item;
						// search products and add_ons for the sale
						const sale =
							this.find(cart.products, { id: saleId, productType: `SALE` }) ||
							this.find(cart.add_ons, { id: saleId, type: `ADD_ON` });
						const productType = this.isInt(holdId) ? `HOLD` : `SALE`;

						let deliveryConfiguration;

						if (productType === `SALE`) {
							deliveryConfiguration = sale.deliveryConfiguration;
						} else if (productType === `HOLD`) {
							const hold = this.find(cart.products, { id: holdId, productType });
							const assignment = this.find(hold.saleAssignments, { saleId });
							deliveryConfiguration = assignment.deliveryConfiguration;
						}

						if (this.isObject(deliveryConfiguration)) {
							const { additionalDeliveryType, deliveryType } = deliveryConfiguration;

							if (this.includes([deliveryType, additionalDeliveryType], `SHIPPING`)) {
								requirements.shipping_address = true;
							}
						}

						if (this.isArray(conflicts, 1)) {
							// a tier-change was detected, notify the user
							let message = `Items in your cart may have changed, please verify ${item.name}`;
							if (this.includes(conflicts, `NAME`)) {
								if (this.includes(conflicts, `PRICE`)) {
									message += ` name and price`;
								}
								message += `.`;
							} else {
								message += ` price.`;
							}

							App.notifyUser({
								type: `warning`,
								message,
								duration: 0,
							});
						}
					});

					cart.saleItems = saleItems;

					if (this.isArray(response.add_ons, 1)) {
						Events.normalizeSales({
							sales: response.add_ons,
							event,
						});

						this.each(response.add_ons, (add_on) => {
							var product = this.find(cart.products, {
								id: add_on.id,
							});

							if (!this.isObject(product)) {
								// cart-specific normalizing
								this.initProduct(add_on);
								add_on.productType = `SALE`;

								cart.products.push(add_on);
							}
						});
					}

					if (q_required) {
						switch (
							cart.queue_settings.delivery // check for special shipping settings
						) {
							case `SHIPPING_WILLCALL`:
							case `ALL_WILLCALL`: // override shipping, don't ask user for shipping address
								requirements.shipping_address = false;
								break;
							case `DEFAULT`:
							default:
							// no special handling
						}
					}

					requirements.q_config = q_required;

					if (response.paymentPlanRequired === true) {
						requirements.payment_plan = true;
						features.payment_plan_optional = false;
					}

					if (!requirements.payment_plan && urlParams.ppr === `1`) {
						requirements.payment_plan = true;
						features.payment_plan_optional = true;
					}

					if (this.isArray(custom_form_assignments)) {
						data.custom_forms = [];

						const path = App.getPath();
						const formIds = this.map(custom_form_assignments, `customFormId`);
						const uniqueFormIds = this.unique(formIds);
						const formPromises = this.map(uniqueFormIds, (formId) => XHR.ajax({ url: `/api/custom-forms/${formId}` }));
						const sortedAssignments = Utils.sortBy(custom_form_assignments, `priority`);
						const forms = [];

						promise = this.when({
							promises: formPromises,
							then: (formResponses) => {
								this.each(sortedAssignments, (details) => {
									// iterate over each form
									const { customFormId: formId, saleOrderLocalIds } = details;
									const form = this.find(formResponses, { id: formId });

									if (this.isArray(saleOrderLocalIds)) {
										// this is a per-ticket form, store details about which ticket(s) to target
										this.each(saleOrderLocalIds, ({ holdId, priceLevelOptionId, priceListId, saleId, tierId }) => {
											// iterate over each set of values the form applies to
											const formClone = this.clone(form); // shield the original response object, in case of re-use
											this.assign(formClone, { holdId, priceLevelOptionId, priceListId, saleId, tierId }); // make future lookups easier
											forms.push(formClone);
										});
									} else {
										const formClone = this.clone(form); // shield the original response object, in case of re-use
										forms.push(formClone);
									}
								});

								// all requested forms are ready
								this.each(forms, (form) => {
									const { collection_type, id, title } = form;
									const questions = Utils.sortBy(form.questions, ({ ordinal }) => ordinal);
									const friendlyUrl = App.friendlyUrl(`${id}-${title}`);

									form.trackingUrl = `${path}/forms/${friendlyUrl}`;
									form.trackingTitle = `Form ${id}: ${title}`;
									form.questions = questions;

									switch (collection_type) {
										case `TICKET`: // a per-ticket form, expand into multiple form pages
											{
												const { holdId, priceLevelOptionId, priceListId, saleId, tierId } = form;
												const saleItemQuery = { holdId, priceLevelOptionId, priceListId, saleId, tierId };
												const saleItem = this.find(cart.saleItems, saleItemQuery);
												const { name, quantity } = saleItem;
												const sale = this.find(cart.products, { id: saleId, productType: `SALE` });
												const { category } = sale;
												const iconName = Config.SALE_CATEGORY_ICONS[category];
												const indices = Utils.range(0, quantity); // e.g. quantity 3 => [0, 1, 2]

												this.each(indices, (i) => {
													const { initial_blurb } = form;
													const internalId = Utils.uniqueId();
													const formQuestions = this.clone(questions);
													let blurb;
													let icon;

													// one page per ticket of this type
													blurb = this.isString(initial_blurb, 1) ? `${initial_blurb}<br />` : ``;
													icon = Templates.getIcon(iconName);
													if (this.isString(icon, 1)) {
														blurb += icon;
													}
													blurb += quantity > 1 ? `${name} #` + (i + 1) : name;

													const newForm = this.assign({ internalId }, form, {
														// copy all form properties over, then extend on top
														initial_blurb: blurb,
													});

													this.extract(formQuestions, (question) => {
														// iterate over the form questions
														if (question.type === `LEGAL`) {
															// legal questions should be split into their own forms
															const formClone = this.clone(newForm);
															formClone.questions = [question];
															data.custom_forms.push(formClone);
															return true; // remove from formQuestions array
														}
													});

													if (formQuestions.length > 0) {
														// there are non-LEGAL questions left over
														newForm.questions = formQuestions;
														data.custom_forms.push(newForm);
													}
												});
											}
											break;

										default: {
											// not a special kind of form, pass through as-is
											const formQuestions = this.clone(questions);

											form.internalId = Utils.uniqueId();

											this.extract(formQuestions, (question) => {
												// iterate over the form questions
												if (question.type === `LEGAL`) {
													// legal questions should be split into their own forms
													const formClone = this.clone(form);
													formClone.questions = [question];
													data.custom_forms.push(formClone);
													return true; // remove from formQuestions array
												}
											});

											if (formQuestions.length > 0) {
												// there are non-LEGAL questions left over
												form.questions = formQuestions;
												data.custom_forms.push(form);
											}
										}
									}
								});
							},
						});
					}

					if (data.enablePersistentCheckout && this.isString(cart.token, 1)) {
						Storage.set({ key: `cart_token`, value: cart.token });
						Storage.set({ key: `cart_expiration`, value: cart.expiration });
						data.initialCartToken = cart.token;
					}

					return this.when({
						promise,
						then: () => this.clone(cart), // ultimate resolve with the cart
						fin: this.reconcile,
					});
				},
			},

			reconcile: {
				value() {
					const SELECTABLE_DISPLAY_STATES = [`OPEN`, `RESALE`];
					const { Events, Templates, data } = this;
					const { cart, event, user } = data;
					const saleCategories = [];
					const printNumber = Templates.getHelper(`printNumber`);
					let minimumNotSelected = false; // used to detect the minimum not selected violation
					let invalidMultiple = false; // used to detect invalid ticket quanties
					let recalculate = false;
					let countedTicketTypes;
					let countedSaleTypes;
					let countedQuantity;
					let singleProductType; // used in cases of "queueBehavior === SINGLE_SALE"
					let singleProductId; // used in cases of "queueBehavior === SINGLE_SALE"
					let queueRequired;
					let cartQuantity;
					let isCounted;
					let subtotal;

					if (!this.isObject(event) || !this.isObject(cart)) {
						// not initialized
						return; // nothing to do
					}

					const { checkoutMode } = data;
					const { addOnPacks, guestCheckoutBehavior, products, saleItems, unlockedHolds, token, violations } = cart;
					const checkoutInProgress = this.isString(token, 1); // used to avoid making changes once an order has been reserved from the server
					const { mostca, mostco } = event; // maxOnlineSaleTypeCategories, maxOnlineSaleTypes
					const hasAddOnPacks = this.isArray(addOnPacks, 1);
					let { agentCheckoutPaymentOptions: tenderTypeOptions } = cart;

					if (!this.isArray(tenderTypeOptions, 1)) {
						tenderTypeOptions = [`CREDIT_CARD`];
					}

					// reset selected add-on pack counts
					if (hasAddOnPacks) {
						this.each(addOnPacks, (pack) => {
							pack.selectedSaleTypes = new window.Set();
						});
					}

					countedSaleTypes = event.maxTicketsAllowedMetadata; // ADD_ON | FIXED | PRIVATE
					countedSaleTypes = this.isString(countedSaleTypes, 1) ? countedSaleTypes.split(`,`) : null;
					if (this.isNull(countedSaleTypes)) {
						isCounted = () => true;
					} else {
						isCounted = (saleType) => this.includes(countedSaleTypes, saleType);
					}

					countedQuantity = 0; // includes past purchases
					this.each(products, (product) => {
						const { state } = product;

						// reset counts and state overrides
						product.totalQuantity = 0;
						product.displayState = state;
						product.localState = state;
					});

					this.each(unlockedHolds, (hold) => {
						const { id } = hold;
						const productType = `HOLD`;
						const query = { id, productType };
						const match = this.find(products, query);

						if (this.isObject(match)) {
							const { state } = match;
							const { state: newState } = hold;

							this.assign(match, hold); // overwrite all properties present on the unlocked version
							// store the up-to-date state in displayState, original in state
							match.displayState = newState;
							match.localState = newState;
							match.state = state; // preserve original state for comparison
							match.isUnlocked = true;
						} else {
							const clone = this.clone(hold);
							this.initProduct(clone);
							clone.state = `LOCKED`; // NOTE: important for detecting that the hold was unlocked via access code
							products.push(clone);
						}
					});

					if (checkoutMode === `AGENT`) {
						if (this.isString(guestCheckoutBehavior)) {
							// specific checkout behavior was returned from server
							const isAgentCheckoutAllowed = this.includes([`ALL`, `AGENT_ONLY`], guestCheckoutBehavior);
							cart.checkoutMode = isAgentCheckoutAllowed ? `AGENT` : `USER`;
						} else {
							// still shopping, judge using event settings
							const { guestCheckoutSetting } = event;
							const isAgentCheckoutAllowed = this.includes(guestCheckoutSetting, `AGENT`);
							cart.checkoutMode = isAgentCheckoutAllowed ? `AGENT` : `USER`;
						}
					} else {
						cart.checkoutMode = checkoutMode;
					}

					if (this.isObject(user)) {
						const { purchasedHoldQuantityMap, purchasedSaleQuantityMap } = user;
						const { id: eventId, saleTypeMap } = event;
						const eventKey = `${eventId}`;

						// TODO: Get purchasedHoldQuantityMap and purchasedSaleQuantityMap from new endpoints once BE has added them (required before release to prod)
						const holdMap = purchasedHoldQuantityMap ? purchasedHoldQuantityMap[eventKey] : [];
						const saleMap = purchasedSaleQuantityMap ? purchasedSaleQuantityMap[eventKey] : [];

						this.each(saleMap, (saleQuantity, saleKey) => {
							const productType = `SALE`;
							const saleId = this.parseInt(saleKey);
							const product = this.find(products, { id: saleId, productType });
							const saleType = saleTypeMap[saleId];
							let countTowardsEventMax = true;

							// not guaranteed to be present for ADD_ONs, etc.
							if (this.isObject(product)) {
								if (product.ignorePurchasedQuantity) {
									// past purchases should not counted toward any limits
									countTowardsEventMax = false;
								} else {
									// past purchases count for sale max limit
									product.totalQuantity += saleQuantity;
								}
							}

							if (countTowardsEventMax && isCounted(saleType)) {
								// past purchases count toward the event total max limit
								countedQuantity += saleQuantity;
							}
						});

						this.each(holdMap, (saleQuantity, holdKey) => {
							const productType = `HOLD`;
							const holdId = parseInt(holdKey, 10);
							const product = this.find(products, { id: holdId, productType });

							if (this.isObject(product)) {
								product.totalQuantity += saleQuantity;
							}

							// NOTE: hold orders are counted in the sale map, don't double-increment event total
						});
					}

					if (this.isInt(event.maxTicketsAllowed)) {
						cart.maxTicketsAllowed = event.maxTicketsAllowed - countedQuantity;
					}

					countedTicketTypes = 0; // used for event.mostco validation
					cartQuantity = 0; // current cart listing, ignore past purchases and other rules
					subtotal = 0; // local calculation of base order cost
					queueRequired = false;
					this.each(products, (product) => {
						const productQuantity = product.quantity;
						const { isReserved, productType, selections, state, totalQuantity, trackingId } = product;
						const stateOverride = stateOverrides[trackingId];

						// keep track of the count from the purchase history
						product.purchasedQuantity = totalQuantity;

						if ((state === `LOCKED` || state === `HIDE`) && productType === `SALE`) {
							// check for unlocks that may augment this product
							const saleId = product.id;
							const unlock = this.find(cart.saleUnlocks, { saleId });

							if (this.isObject(unlock)) {
								if (unlock.status === `NO_LOCK` || unlock.status === `UNLOCKED`) {
									// the sale was unlocked
									const { state: newState, metadataCollection } = unlock;
									product.displayState = newState;
									product.localState = newState;
									product.metadataCollection = metadataCollection;
									product.isUnlocked = true;
								}
							}
						}

						if (product.isUnlocked !== true) {
							product.isUnlocked = false;
						}

						// NOTE: calculate before the state override is applied below, for local QUEUE override, etc.
						product.isSelectable = isReserved && SELECTABLE_DISPLAY_STATES.includes(product.displayState);

						if (this.isString(stateOverride, 1)) {
							// a manual override has been set
							product.displayState = stateOverride;
							product.localState = stateOverride;
							product.isStateOverride = true;
						} else {
							product.isStateOverride = false;
						}

						if (productQuantity > 0) {
							const seatSelections = this.filter(selections, ({ seatLabel, state }) => {
								return state === `ACTIVE` && this.isString(seatLabel);
							});
							const isQueued = product.displayState === `QUEUED`;

							if (isQueued || !product.isReserved || seatSelections.length === productQuantity) {
								cartQuantity += productQuantity; // plain tally
							}

							product.totalQuantity += productQuantity; // total count of this sale purchased, to check against limits
							if (isCounted(product.type)) {
								countedQuantity += productQuantity; // total event count, to check against limits
							}

							if (isQueued) {
								queueRequired = true;

								if (data.queueBehavior === `SINGLE_SALE`) {
									singleProductType = product.productType;
									singleProductId = product.id;
								}
							}

							this.each(selections, ({ price }) => (subtotal += price));

							if (productType === `SALE`) {
								saleCategories.push(product.category);
							} else if (productType === `HOLD`) {
								this.each(selections, (selection) => {
									const { saleId } = selection;

									if (this.isInt(saleId, 0)) {
										// an underlying sale is associated, use its category
										const sale = this.find(products, { productType: `SALE`, id: saleId });
										saleCategories.push(sale.category);
									} else {
										// no underlying sale yet, use default category
										saleCategories.push(product.category);
									}
								});
							}

							// check for a max number of ticket types that can be in cart
							if (this.isInt(mostco, 1) && !product.isAddOn) {
								let included = true;

								if (this.isArray(mostca, 1)) {
									// limit this restriction to the provided list of categories
									included = mostca.includes(product.category);
								}

								if (included && product.quantity > 0) {
									// Ticket type matches and is in cart
									if (countedTicketTypes < mostco) {
										// limit not yet reached, increment the count
										countedTicketTypes++;
									} else {
										// max ticket types has already been hit, remove from cart
										product.quantity = 0;
										product.selections = [];
										recalculate = true;
									}
								}
							}

							if (hasAddOnPacks && product.isAddOn) {
								this.each(product.addOnPackIndexes, (index) => {
									addOnPacks[index].selectedSaleTypes.add(product.id);
								});
							}
						} else {
							selections.length = 0; // clear out any selections
						}
					});

					if (!this.isString(cart.token)) {
						cart.requirements.q_config = queueRequired;
					}

					if (subtotal === 0) {
						const allCategories = this.unique(saleCategories);

						if (this.isEqual(allCategories, [`GUEST`])) {
							cart.checkoutLabel = `Add to list`;
						} else if (cart.requirements.q_config) {
							cart.checkoutLabel = `Join waitlist`;
						} else {
							cart.checkoutLabel = `Checkout`;
						}

						cart.checkoutAmount = `Free`;
					} else if (cart.requirements.q_config) {
						cart.checkoutLabel = `Join waitlist`;
						cart.checkoutAmount = printNumber(subtotal, `currencyCompact`, cart.currency);
					} else {
						cart.checkoutLabel = `Checkout`;
						cart.checkoutAmount = printNumber(subtotal, `currencyCompact`, cart.currency);
					}

					if (this.isArray(saleItems, 1)) {
						// a saleItems array was received from the server, recalculate summary
						// NOTE: important for cases like ticket reservations, where products[i] selections can't be relied on
						cartQuantity = 0;
						subtotal = 0;
						this.each(saleItems, (item) => {
							cartQuantity += item.quantity;
							subtotal += item.quantity * item.price;
						});
					}

					this.assign(cart, {
						quantity: cartQuantity,
						subtotal,
						tenderTypeOptions,
					});

					this.each(products, (product) => {
						// NOTE: separate loop from above, to get accurate quantity counts first
						const { displayState, isLevels, maxTicketsAllowed, quantity, selections, type } = product;
						const canUpdate = !checkoutInProgress || type === `ADD_ON`;
						const inCart = quantity > 0;

						let { localState, ticketMultiple } = product;

						if (isLevels) {
							// override ticketMultiple behavior if price levels are enabled
							ticketMultiple = 1;
						}

						switch (displayState) {
							case `RESALE`: {
								break;
							}

							case `QUEUED`:
							case `OPEN`: {
								// Ticket is active for purchase
								const isQueued = displayState === `QUEUED`;

								localState = displayState; // default for non-QUEUED
								if (this.isInt(maxTicketsAllowed, 1)) {
									const increased = product.totalQuantity + ticketMultiple;

									if (increased > maxTicketsAllowed) {
										localState = `AT_LIMIT`; // max limit hit for sale

										if (canUpdate && inCart) {
											if (product.totalQuantity > product.maxTicketsAllowed) {
												// more than are allowed
												const difference = product.totalQuantity - product.maxTicketsAllowed;

												if (difference > product.quantity) {
													product.quantity = 0;
												} else {
													let updatedQuantity = product.quantity - difference;

													while (updatedQuantity > 0 && updatedQuantity % product.ticketMultiple !== 0) {
														selections.pop();
														updatedQuantity--;
													}

													product.quantity = updatedQuantity;
												}

												recalculate = true;
											}
										}
									}
								}

								if (this.isInt(event.maxTicketsAllowed, 0) && isCounted(product.type)) {
									// this check only applies to counted types
									// NOTE: Use explicit ticketMultiple references, local variable may have been overidden to 1
									let numberToAdd;

									if (this.isInt(product.minTicketsAllowed, 0) && product.quantity < product.minTicketsAllowed) {
										const { quantity: saleQuantity } = product;

										numberToAdd = 0;
										while (saleQuantity + numberToAdd < product.minTicketsAllowed) {
											numberToAdd += product.ticketMultiple;
										}
									} else {
										numberToAdd = product.ticketMultiple;
									}

									const increasedTotalQuantity = countedQuantity + numberToAdd;

									if (increasedTotalQuantity > event.maxTicketsAllowed) {
										// can't increase without going over, event max hit
										localState = `AT_LIMIT`;
									}
								}

								if (data.queueBehavior === `SINGLE_SALE`) {
									if (this.isInt(singleProductId)) {
										if (product.id !== singleProductId || product.productType !== singleProductType) {
											// a QUEUED ticket is in cart, all other tickets are disabled
											localState = `AT_Q_LIMIT`;
										}
									}

									if (displayState === `QUEUED` && cartQuantity > product.quantity) {
										// a non-QUEUED ticket is in cart, all QUEUED are disabled
										if (product.quantity > 0) {
											product.selections = [];
											product.quantity = 0;
											recalculate = true;
										}

										localState = `AT_Q_LIMIT`;
									}
								}

								if (
									this.isInt(mostco, 1) && // a max number of online sales is set
									countedTicketTypes >= mostco && // the limit has been reached
									product.quantity === 0 && // not already in cart
									!product.isAddOn // not an add-on
								) {
									let included = true;

									if (this.isArray(mostca, 1)) {
										// limit this restriction to the provided list of categories
										included = mostca.includes(product.category);
									}

									if (included) {
										// max ticket type limit has been hit, but sale isn't already in cart, disable
										localState = isQueued ? `AT_Q_LIMIT` : `AT_LIMIT`;
									}
								}

								if (hasAddOnPacks && product.isAddOn) {
									const packAtMaxLimit = product.addOnPackIndexes.find((index) => {
										const pack = addOnPacks[index];
										// can add more of already selected add-ons, but can't add new types if max types is hit
										return (
											!this.isNull(pack.maxNumberOfSaleTypes) &&
											pack.maxNumberOfSaleTypes <= pack.selectedSaleTypes?.size &&
											!pack.selectedSaleTypes.has(product.id)
										);
									});

									if (this.isInt(packAtMaxLimit)) {
										localState = `AT_LIMIT`;
									}
								}
								break;
							}

							case `LOCKED`: // Needs to be unlocked
							case `CLOSED`: // Sale ended check
							case `PRE`: // Sale hasn't started
							case `HIDE`: // Sale being manually hidden
							case `SOLD_OUT`: // sold out check
							case `AVAIL_AT_DOOR`: // manual override state
							case `AVAIL_SOON`: // manual override state
							case `FREE`: // used for non-tickets
								if (canUpdate && inCart) {
									product.selections = [];
									product.quantity = 0; // reset in case it transitioned from an available to non-available state
									recalculate = true;
								}
								break;

							default:
								// unknown state
								this.log({
									type: `warning`,
									message: `Unrecognized sale state: ${product.state}`,
								});
						}

						if (product.localState !== localState) {
							product.localState = localState;
						}

						if (displayState === `RESALE`) {
							product.tag = `Resale`;
						} else if (displayState === `QUEUED`) {
							product.tag = `Waitlist`;
						} else if (displayState === `SOLD_OUT`) {
							if (!this.isString(product.optionalSaleRedirectUrl)) {
								product.tag = `Sold Out`;
							}
						} else if (displayState === `CLOSED`) {
							product.tag = `Sale Over`;
						} else if (displayState === `LOCKED`) {
							product.tag = `Code Needed`;
						} else if (displayState === `AVAIL_AT_DOOR`) {
							product.tag = `Sold at Door`;
						} else if (displayState === `OPEN` && this.isString(product.countdownMessage, 1)) {
							product.tag = product.countdownMessage;
						} else {
							product.tag = null;
						}

						if (canUpdate && this.isInt(singleProductId) && inCart) {
							if (product.id !== singleProductId || product.productType !== singleProductType) {
								product.selections = [];
								product.quantity = 0;
								recalculate = true;
							}
						}
					});

					const allHolds = this.filter(products, { productType: `HOLD` });

					this.each(products, (product) => {
						const { isLevels, priceRegulationBreakout, productType, quantity, ticketMultiple } = product;
						let isProductRemovable = quantity > 0; // sensible default
						let isProductAddable; // decided using state as a last step

						if (productType === `SALE`) {
							const potentialHolds = this.filter(allHolds, ({ saleAssignments }) => {
								const assignment = this.find(saleAssignments, { saleId: product.id });
								return this.isObject(assignment);
							});

							// used for checking for valid quantity selections, across this sale and any related holds
							let totalQuantity = quantity;

							this.each(potentialHolds, (hold) => {
								this.each(hold.selections, (selection) => {
									if (selection.saleId === product.id || selection.saleId === null) {
										// current sale is selected, or unselected for F.B.A. and should potentially count
										totalQuantity++;
									}
								});
							});

							if (isLevels) {
								// price levels enabled
								const { priceLevels, selections } = product;
								let removableLevelCount = 0;

								// traverse the individual levels for minBehavior
								this.each(priceLevels, (priceLevel) => {
									const { minBehavior, minTicketsAllowed, value: priceLevelOptionId } = priceLevel;
									const levelSelections = this.filter(selections, { priceLevelOptionId });
									const unselected = this.filter(selections, { priceLevelOptionId: null });
									const { length: levelQuantity } = levelSelections;
									const { length: unselectedQuantity } = unselected;
									let quantityFloor = 0;
									let isLevelAddable = true;
									let isLevelSelectable = true;
									let isLevelRemovable;

									if (this.isInt(minTicketsAllowed, 1)) {
										if (minBehavior === `ENFORCE`) {
											quantityFloor = minTicketsAllowed;

											if (levelQuantity < minTicketsAllowed) {
												// zero is not allowed, purchase is required
												minimumNotSelected = true;
											}
										} else {
											// minBehavior = SET_AS_DEFAULT or null
											if (levelQuantity < minTicketsAllowed && levelQuantity > 0) {
												// zero is allowed, since the purchase is optional
												minimumNotSelected = true;
											}
										}

										if (levelQuantity < minTicketsAllowed) {
											// level quantity is below the minimum, check whether incrementing would exceed any limits
											const difference = minTicketsAllowed - levelQuantity;

											// check for a product-level maximum
											if (this.isInt(product.maxTicketsAllowed, 0)) {
												const maxAddableQuantity = product.maxTicketsAllowed - quantity;
												const maxSelectableQuantity = maxAddableQuantity + unselectedQuantity;

												if (maxAddableQuantity < difference) {
													isLevelAddable = false;
												}

												if (maxSelectableQuantity < difference) {
													isLevelSelectable = false;
												}
											}

											// check for an event-level maximum
											if (this.isInt(cart.maxTicketsAllowed, 0)) {
												const maxCartAddableQuantity = cart.maxTicketsAllowed - cart.quantity;
												const maxCartSelectableQuantity = maxCartAddableQuantity + unselectedQuantity;

												if (maxCartAddableQuantity < difference) {
													isLevelAddable = false;
												}

												if (maxCartSelectableQuantity < difference) {
													isLevelSelectable = false;
												}
											}
										}
									}

									isLevelRemovable = levelQuantity > quantityFloor;

									if (isLevelRemovable) {
										removableLevelCount++;
									}

									this.assign(priceLevel, {
										isAddable: isLevelAddable,
										isRemovable: isLevelRemovable,
										isSelectable: isLevelSelectable,
									});
								});

								if (removableLevelCount > 0) {
									isProductRemovable = true;
								} else {
									// NOTE: handles cases where FBA selections are in cart, but no levels are selected
									this.find(selections, ({ state }) => {
										if (state === `FBA`) {
											isProductRemovable = true;
											return true; // break loop
										}
									});
								}

								if (this.isInt(product.minTicketsAllowed, 1)) {
									// ignore sale-level minBehavior, always treat as null/default

									if (totalQuantity < product.minTicketsAllowed && totalQuantity > 0) {
										minimumNotSelected = true;
									}
								}
							} else {
								// standard pricing, minBehavior is at the sale level
								const { minBehavior, minTicketsAllowed, ticketMultiple } = product;
								let quantityFloor = 0;

								if (this.isInt(minTicketsAllowed, 1)) {
									if (minBehavior === `ENFORCE`) {
										quantityFloor = minTicketsAllowed;

										if (totalQuantity < minTicketsAllowed) {
											// zero is not allowed, purchase is required
											minimumNotSelected = true;
										}
									} else {
										// minBehavior === `SET_AS_DEFAULT` | null
										if (totalQuantity > 0 && totalQuantity < minTicketsAllowed) {
											// zero is allowed, since the purchase is optional
											minimumNotSelected = true;
										}
									}
								}

								if (quantity - ticketMultiple < quantityFloor) {
									isProductRemovable = false;
								}
							}

							if (totalQuantity % ticketMultiple !== 0) {
								invalidMultiple = true;
							}
						} else if (productType === `HOLD`) {
							const { saleAssignments, selections, totalQuantity } = product;
							const allDisplayStates = []; // used for deciding whether to override state based on assignments

							let { ticketMultiple } = product;
							let selectedMaxQuantity = 0; // limit if accumulating selected tickets from the chart
							let fbaMaxQuantity; // limit if using + button for Find Best Available functionality
							let selectedSales;

							// HOLDs are always reserved, don't need to worry about ADD_ON + minBehavior ENFORCE
							isProductRemovable = quantity > 0;

							if (isLevels) {
								const { priceLevels } = product;
								let removableLevelCount = 0; // used as an extra check for the overall product removable
								let addableLevelCount = 0; // used as an extra check for the overall product addable

								// override ticketMultiple behavior if price levels are enabled
								ticketMultiple = 1;

								this.each(priceLevels, (priceLevel) => {
									const { value: priceLevelOptionId } = priceLevel;
									const levelSelections = this.filter(product.selections, { priceLevelOptionId });
									const unselected = this.filter(selections, { priceLevelOptionId: null });
									const { length: levelQuantity } = levelSelections;
									const { length: unselectedQuantity } = unselected;
									const { saleAssignments } = product;
									let isLevelSelectable = true;
									let isLevelRemovable = false;
									let isLevelAddable = true;
									let numberToAdd = 1;
									let holdMaxTicketsAllowed;

									if (levelQuantity > 0) {
										// HOLDs are always reserved, don't need to worry about ADD_ON + minBehavior
										isLevelRemovable = true;
									}

									this.each(saleAssignments, (assignment) => {
										const sale = this.find(products, { productType: `SALE`, id: assignment.saleId });
										const { maxTicketsAllowed } = sale;
										const salePriceLevel = this.find(sale.priceLevels, { value: priceLevelOptionId });
										const saleSelections = this.filter(sale.selections, { priceLevelOptionId });
										const { length: saleLevelQuantity } = saleSelections;
										const { minTicketsAllowed } = salePriceLevel;

										if (this.isInt(minTicketsAllowed, 2)) {
											const saleNumberToAdd = minTicketsAllowed - saleLevelQuantity - levelQuantity;

											if (saleNumberToAdd > numberToAdd) {
												numberToAdd = saleNumberToAdd;
											}
										}

										if (this.isInt(maxTicketsAllowed)) {
											if (holdMaxTicketsAllowed === undefined || maxTicketsAllowed < holdMaxTicketsAllowed) {
												holdMaxTicketsAllowed = maxTicketsAllowed;
											}
										}
									});

									if (quantity + numberToAdd > holdMaxTicketsAllowed) {
										// incrementing would exceed an assigned sale's maxTicketsAllowed
										isLevelAddable = false;
									}

									if (quantity - unselectedQuantity + numberToAdd > holdMaxTicketsAllowed) {
										// incrementing would exceed an assigned sale's maxTicketsAllowed
										isLevelSelectable = false;
									}

									if (this.isInt(cart.maxTicketsAllowed)) {
										if (cart.quantity + numberToAdd > cart.maxTicketsAllowed) {
											// incrementing would exceed event maxTicketsAllowed
											isLevelAddable = false;
										}

										if (cart.quantity - unselectedQuantity + numberToAdd > cart.maxTicketsAllowed) {
											// incrementing would exceed event maxTicketsAllowed
											isLevelSelectable = false;
										}
									}

									if (isLevelAddable) {
										addableLevelCount++;
									}

									if (isLevelRemovable) {
										removableLevelCount++;
									}

									this.assign(priceLevel, {
										isAddable: isLevelAddable,
										isRemovable: isLevelRemovable,
										isSelectable: isLevelSelectable,
									});
								});

								isProductAddable = addableLevelCount.length > 0;
								isProductRemovable = removableLevelCount > 0;
							} else {
								// standard pricing, use sale-level minBehavior

								const { minBehavior, minTicketsAllowed, ticketMultiple } = product;
								const enforceMinimum = this.isInt(minTicketsAllowed, 1) && minBehavior === `ENFORCE`;
								const quantityFloor = enforceMinimum ? minTicketsAllowed : 0;

								if (quantity - ticketMultiple >= quantityFloor) {
									isProductRemovable = true;
								}
							}

							this.each(saleAssignments, ({ saleId: id }) => {
								const sale = this.find(products, { id, productType: `SALE` });
								const { displayState, maxTicketsAllowed } = sale;

								allDisplayStates.push(displayState);

								if (this.isInt(maxTicketsAllowed)) {
									// sale has a limit
									if (this.includes([`OPEN`, `HIDE`], displayState)) {
										// sale is available for seating chart selection
										const { purchasedQuantity, quantity: saleQuantity } = sale;
										const userMaxTicketsAllowed = maxTicketsAllowed - purchasedQuantity;
										const adjustedMaxTicketsAllowed = userMaxTicketsAllowed - saleQuantity;

										if (selectedMaxQuantity !== null) {
											selectedMaxQuantity += userMaxTicketsAllowed;
										}

										if (this.isUndefined(fbaMaxQuantity) || adjustedMaxTicketsAllowed < fbaMaxQuantity) {
											fbaMaxQuantity = adjustedMaxTicketsAllowed;
										}
									}
								} else {
									selectedMaxQuantity = null;
								}
							});

							if (product.displayState === `OPEN` && allDisplayStates.length > 0) {
								const hasOpenSale = this.includes(allDisplayStates, `OPEN`);
								const hasHiddenSale = this.includes(allDisplayStates, `HIDE`);

								if (!hasOpenSale && !hasHiddenSale) {
									// should override OPEN displayState, to disallow Find Best Available, etc.
									let [overrideState] = allDisplayStates;

									if (overrideState === `QUEUED`) {
										// NOTE: holds do not support waitlist functionality
										// keep the hold in open state, since it's possible seats to be released and become available, but sales don't toggle back from QUEUED
										overrideState = `OPEN`;
									}

									if (product.displayState === product.localState) {
										// Avoid overriding situations like AT_LIMIT
										product.localState = overrideState;
									}

									product.displayState = overrideState;
								}
							}

							if (product.displayState === `OPEN` || product.displayState === `QUEUED`) {
								// it's possible for items to be in cart for this product
								selectedSales = this.filter(selections, ({ seatLabel }) => this.isString(seatLabel));
								if (selectedSales.length > 0) {
									// seat selections are present from interacting with the chart
									if (this.isInt(selectedMaxQuantity, 0)) {
										if (totalQuantity + ticketMultiple > selectedMaxQuantity) {
											product.localState = `AT_LIMIT`;
										}
									}
								} else {
									// either empty or F.B.A. quantity
									if (this.isInt(fbaMaxQuantity, 0)) {
										if (totalQuantity + ticketMultiple > fbaMaxQuantity) {
											product.localState = `AT_LIMIT`;
										}
									}
								}
							}

							product.fbaMaxQuantity = fbaMaxQuantity;
						}

						isProductAddable = this.includes([`OPEN`, `QUEUED`], product.localState);

						let summary;
						let priceBreakoutMessage;
						switch (product.displayState) {
							case `OPEN`:
							case `QUEUED`:
								summary = product.formattedPrice;
								// price breakout message should only show for open, queued, or resale sales
								priceBreakoutMessage = Events.getPriceBreakoutMessage({
									priceRegulationBreakout,
									currency: cart.currency,
								});
								break;
							case `RESALE`:
								summary = product.formattedPrice;
								break;
							case `AVAIL_SOON`:
								summary = `Available Soon`;
								if (this.includes(event.ticketPickerPriceDisplay, `PRICE_ON_AVAIL_SOON`)) {
									summary += ` - ${product.formattedPrice}`;
								}
								break;
							case `AVAIL_AT_DOOR`:
								summary = `Tickets Available at the Door`;
								break;
							case `CLOSED`:
								summary = `Sale Over`;
								break;
							case `FREE`:
								summary = `Free`;
								break;
							case `LOCKED`:
								summary = `Access Code Needed`;
								if (this.includes(event.ticketPickerPriceDisplay, `PRICE_ON_LOCKED`)) {
									if (this.isString(product.formattedPriceTotal)) {
										summary += ` - ${product.formattedPriceTotal}`;
									} else {
										summary += ` - ${product.formattedPrice}`;
									}
								}
								break;
							case `SOLD_OUT`:
								if (this.isString(product.optionalSaleRedirectUrl, 1)) {
									if (this.includes([`CABANA`, `TABLE_SERVICE`], product.category)) {
										summary = `Reservations`;
									} else {
										summary = `Sold elsewhere`;
									}
								} else {
									summary = `Sold out`;
								}
								break;
							default:
								summary = ``;
						}

						this.assign(product, {
							isAddable: isProductAddable,
							isRemovable: isProductRemovable,
							priceBreakoutMessage,
							summary,
						});
					});

					if (minimumNotSelected) {
						violations.push({
							type: `minimumNotSelected`,
							message: `The minimum number of items was not met. Please select more items.`,
						});
					} else {
						this.extract(violations, { type: `minimumNotSelected` });
					}

					if (invalidMultiple) {
						violations.push({
							type: `multipleNotValid`,
							message: `An invalid number of items was selected. Please adjust your selection to continue.`,
						});
					} else {
						this.extract(violations, { type: `multipleNotValid` });
					}

					cart.requireSavedPayment = cart.usePaymentPlan || cart.requirements.q_config;

					if (this.isArray(cart.upgrades, 1) && this.syncUpgradesWithSeatSelections()) {
						recalculate = true;
					}

					if (recalculate) {
						// skip notifying subscribers, will trigger after re-running
						return this.reconcile();
					} else {
						const { subscriptions } = this;
						const { cachedCart } = data;

						this.App.unblock(upgradesFindBestBlockOptions);

						if (!this.isEqual(cart, cachedCart)) {
							// keep a cached copy of the published version for future comparison
							data.cachedCart = this.clone(cart);

							this.each(subscriptions, ({ handler, owner }) => {
								if (owner.alive) {
									this.defer({
										then: () => {
											var cartCopy = this.clone(cart); // protect the internal cart object
											return handler.call(owner, cartCopy);
										},
									});
								}
							});
						}
					}
				},
			},

			setViolations: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart, ignoreConsecutiveSeatingViolations } = data;
					const { violations } = options;

					if (ignoreConsecutiveSeatingViolations) {
						this.extract(violations, { type: `consecutiveSeats` });
					}

					cart.violations = violations;
					this.reconcile();
				},
			},

			getPaymentIntent: {
				public: true,
				async: true,
				value(context, options) {
					const { data, XHR } = this;
					const { cart, event, user } = data;
					const { checkoutMode, paymentIntent } = cart;

					if (!this.isString(cart.token, 1)) {
						throw new Error(`ShoppingCart.getPaymentIntent() called without a valid cart.`);
					}

					if (!this.isObject(event) || event.paymentProcessorType !== `STRIPE`) {
						throw new Error(`ShoppingCart.getPaymentIntent() called without a valid Stripe event.`);
					}

					if (!this.isObject(user)) {
						throw new Error(`ShoppingCart.getPaymentIntent() called without a valid user.`);
					}

					if (this.isObject(paymentIntent)) {
						return paymentIntent;
					}

					if (this.isNull(setUserPromise)) {
						if (checkoutMode === `USER`) {
							setUserPromise = XHR.ajax({
								// cache a promise, in case it's called again quickly in succession
								// associate user with cart, ensure to get a paymentIntent if necessary
								url: `/api/events/${event.id}/shoppingcarts/${cart.token}?action=set-user`,
								type: `PUT`,
								REST: true,
								data: {},
								then: (response) =>
									this.syncCart({
										response,
										then: () => response.paymentIntent,
									}),
								fail: (reason) => {
									this.init(); // unrecoverable, reset the cart
									throw reason; // throw for further handling
								},
								fin: () => {
									setUserPromise = null; // reset the cached promise
								},
							});
						} else {
							// use new endpoint for guest checkout, ?action=set-user won't work for guests
							setUserPromise = XHR.ajax({
								// cache a promise, in case it's called again quickly in succession
								// associate user with cart, ensure to get a paymentIntent if necessary
								url: `/api/events/${event.id}/shoppingcarts/${cart.token}`,
								type: `GET`,
								then: (response) =>
									this.syncCart({
										response,
										then: () => response.paymentIntent,
									}),
								fail: (reason) => {
									this.init(); // unrecoverable, reset the cart
									throw reason; // throw for further handling
								},
								fin: () => {
									setUserPromise = null; // reset the cached promise
								},
							});
						}
					} else {
						this.log({
							message: `ShoppingCart.getPaymentIntent() tried calling ?action=get-user while a request was still active`,
						});
					}

					return setUserPromise;
				},
			},

			setTenderType: {
				async: true,
				public: true,
				value(context, options) {
					const { XHR, data } = this;
					const { cart, event } = data;
					const { id: eventId } = event;
					const { token } = cart;
					const { tenderType } = options;
					const url = `/api/events/${eventId}/shoppingcarts/${token}?action=update-payment-method`;
					const body = { tenderType };
					const type = `PUT`;
					const REST = true;
					const promise = XHR.ajax({ body, REST, type, url });

					return this.when({
						promise,
						then: (response) =>
							this.syncCart({
								response,
								then: this.reconcile,
							}),
					});
				},
			},

			addPromo: {
				async: true,
				public: true,
				defaults: {
					ignoreErrors: false,
					force: false,
				},
				value(context, options) {
					const { XHR, data } = this;
					const code = options.code.toUpperCase();
					const { cart, event } = data;
					const { force } = options;

					if (buyRequestPlaced && !force) {
						// too late to try a promo
						return;
					}

					return XHR.ajax({
						type: `PUT`,
						REST: true,
						url: `/api/events/${event.id}/shoppingcarts/${cart.token}?action=apply-promo`,
						ignoreErrors: options.ignoreErrors,
						body: { promoCode: code },
						then: (response) => {
							cart.promoCode = code;

							return this.syncCart({
								response,
								then: () => {
									this.reconcile();
									return response;
								},
							});
						},
						fail: (reason) => {
							var docType, json;

							if (this.isObject(reason.responseJSON)) {
								json = reason.responseJSON;
								docType = json.docType;
							}

							switch (docType) {
								case `STANDARD_ERROR`:
									switch (json.code) {
										case 1029: // max number of promo code attempts
											this.init();
											break;
									}
									break;

								default: // Unrecognized docType
							}

							throw reason; // re-throw for the caller to detect
						},
					});
				},
			},

			removePromo: {
				async: true,
				public: true,
				value(context, options) {
					return this.addPromo({
						code: ``,
					});
				},
			},

			setIncompletePromo: {
				public: true,
				value(context, value) {
					// used to track whether the user might have a dangling coupon code
					this.data.incomplete_promo = value;
				},
			},

			updateUserMarketingOptIn: {
				async: true,
				public: true,
				value(context, options) {
					const { eventId, token } = this.data.cart;
					const { userMarketingOptIn } = options;

					return this.XHR.ajax({
						type: `PUT`,
						REST: true,
						url: `/api/events/${eventId}/shoppingcarts/${token}?action=user-marketing-opt-in`,
						data: { userMarketingOptIn },
					});
				},
			},

			addAccessCode: {
				async: true,
				public: true,
				value(context, options) {
					const { accessCode } = options;
					const { Events, XHR, data } = this;
					const { cart, event } = data;
					const { groupSaleToken, unlockedHolds, saleUnlocks } = cart;
					let { eventId } = options;

					if (this.isUndefined(eventId)) {
						eventId = cart.eventId;
					}

					let url = `/api/events/${eventId}/locks/${accessCode}`;

					if (this.isString(groupSaleToken, 1)) {
						url += `?gst=${groupSaleToken}`;
					}

					return XHR.ajax({
						type: `GET`,
						url,
						then: (response) => {
							const { saleLocks, holds, filterConfiguration } = response;

							// remove any existing unlocks from this code
							this.extract(saleUnlocks, { accessCode });
							this.extract(unlockedHolds, { accessCode });

							this.each(saleLocks, (saleLock) => {
								// iterate through each new lock
								const { saleId } = saleLock;
								// NOTE: BE should be clearer, overriding sale.state
								const status = saleLock.lockStatus;
								const state = saleLock.status;
								// filterConfiguration and metadataCollections may be updated by BE
								const metadataCollection = saleLock.metadataCollection;
								const saleUnlock = { accessCode, eventId, metadataCollection, status, saleId, state };

								this.extract(saleUnlocks, { eventId, saleId }); // remove any existing unlocks from this sale
								saleUnlocks.push(saleUnlock); // add the custom reference to this 'unlock'
							});

							Events.normalizeHolds({ event, holds });

							this.each(holds, (hold) => {
								const existing = this.find(unlockedHolds, { id: hold.id });

								// attach the accessCode for easy lookup later, matching saleLock format
								hold.accessCode = accessCode;

								if (this.isObject(existing)) {
									this.assign(existing, hold);
								} else {
									unlockedHolds.push(hold);
								}
							});

							if (this.isObject(filterConfiguration)) {
								cart.filterConfiguration = filterConfiguration;
							}

							this.reconcile();

							return response;
						},
					});
				},
			},

			applyAddOns: {
				async: true,
				public: true,
				value(context, options) {
					const { XHR, data } = this;
					const { cart } = data;
					const { products } = cart;
					const addOns = this.filter(products, ({ quantity, type }) => type === `ADD_ON` && quantity > 0);
					const saleItems = [];

					if ([`next-add-on-pack`, `previous-add-on-pack`].includes(options.action)) {
						return this.reconcile();
					}

					this.each(addOns, (product) => {
						const { deliveryConfiguration, id, productType, selections } = product;
						const holdId = productType === `HOLD` ? id : null;
						let additionalDeliveryType;
						let deliveryType;

						if (this.isObject(deliveryConfiguration)) {
							deliveryType = deliveryConfiguration.deliveryType;
							additionalDeliveryType = deliveryConfiguration.additionalDeliveryType;
						} else {
							deliveryType = null;
							additionalDeliveryType = null;
						}

						this.each(selections, (selection) => {
							const { priceLevelOptionId, saleId, tierId } = selection;
							const saleItem = this.find(saleItems, { holdId, priceLevelOptionId, saleId });

							if (this.isObject(saleItem)) {
								saleItem.quantity++;
							} else {
								saleItems.push({
									additionalDeliveryType,
									deliveryType,
									holdId,
									priceLevelOptionId,
									quantity: 1,
									saleId,
									tierId,
								});
							}
						});
					});

					if (options.action === `decline-add-ons`) {
						return XHR.ajax({
							type: `PUT`,
							REST: true,
							url: `/api/events/${cart.eventId}/shoppingcarts/${cart.token}?action=decline-add-ons`,
							body: {},
							then: () => {
								cart.progress.add_ons = true;
								this.reconcile();
							},
						});
					}

					if (saleItems.length > 0) {
						return XHR.ajax({
							type: `PUT`,
							REST: true,
							url: `/api/events/${cart.eventId}/shoppingcarts/${cart.token}?action=apply-add-ons`,
							body: { saleItems },
							then: (response) => {
								const { reconcile: then } = this;
								cart.progress.add_ons = true;

								return this.syncCart({ response, then });
							},
						});
					} else {
						cart.progress.add_ons = true;
						this.reconcile();
					}
				},
			},

			enablePaymentPlan: {
				async: true,
				public: true,
				value(context) {
					const { App, data } = this;
					const { cart } = data;

					if (cart.usePaymentPlan !== true) {
						return this.XHR.ajax({
							type: `PUT`,
							REST: true,
							url: `/api/events/${data.event.id}/shoppingcarts/${cart.token}?action=apply-payment-plan`,
							data: {
								usePaymentPlan: true,
							},
							then: (response) => {
								// success
								cart.usePaymentPlan = true;
								const paymentPlanFee = this.isObject(cart) && this.isInt(cart.paymentPlanFee) ? cart.paymentPlanFee : 0;

								// fire the "cart-add" event for payment plan
								App.captureInsight({
									eventType: `cart-add`,
									purchaseFlowItem: `Payment Plan`,
									item: {
										id: `payment_plan`,
										name: `Payment Plan`,
										price: paymentPlanFee,
									},
									productList: {},
								});

								return this.syncCart({
									response,
									then: this.reconcile,
								});
							},
						});
					}
				},
			},

			addOrderProtection: {
				async: true,
				public: true,
				async value(context, options) {
					const { data, XHR } = this;
					const { quote, token } = options;
					const { cart, event } = data;
					const { id: eventId } = event;
					const { orderProtectionConfiguration, token: cartToken } = cart;
					const { offeringId, offeringName } = orderProtectionConfiguration;
					const isProtected = true;
					const orderProtection = { quote, token, offeringId, offeringName, isProtected };
					const url = `/api/events/${eventId}/shoppingcarts/${cartToken}?action=order-protection-opt-in`;
					const body = { orderProtection };
					const type = `PUT`;
					const REST = true;

					const response = await XHR.ajax({ body, REST, type, url });
					cart.orderProtectionAdded = true;
					await this.syncCart({ response });
				},
			},

			confirmOrderProtection: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const { progress } = cart;

					// mark the insurance step complete
					progress.order_protection = true;
					this.reconcile();
				},
			},

			revisitOrderProtection: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const { progress } = cart;

					// mark the insurance step complete
					progress.order_protection = false;
					this.reconcile();
				},
			},

			removeOrderProtection: {
				async: true,
				public: true,
				async value(context, options) {
					const { data, XHR } = this;
					const { cart, event } = data;
					const { id: eventId } = event;
					const { token: cartToken } = cart;
					const url = `/api/events/${eventId}/shoppingcarts/${cartToken}?action=order-protection-opt-out`;
					const REST = true;
					const body = {};
					const type = `PUT`;
					const response = await XHR.ajax({ body, REST, type, url });

					cart.orderProtectionAdded = false;

					await this.syncCart({ response });
				},
			},

			disableOrderProtection: {
				public: true,
				value(context) {
					const { data } = this;
					const { cart } = data;
					const { orderProtection } = cart;

					cart.requirements.order_protection = false;
					cart.progress.order_protection = false;
					data.skipOrderProtection = true;

					if (this.isObject(orderProtection)) {
						this.removeOrderProtection();
					} else {
						this.reconcile();
					}
				},
			},

			isValidSelection: {
				value({ product, selection }) {
					const { isLevels, isReserved } = product;

					if (isReserved) {
						// seatLabel value required for every selection
						if (!this.isString(selection.seatLabel)) {
							return false;
						}
					}

					if (isLevels) {
						// price level required for every selection
						if (!this.isInt(selection.priceLevelOptionId)) {
							return false;
						}
					}

					return true;
				},
			},

			updateProducts: {
				async: true,
				public: true,
				defaults: {
					removeSeats: true,
				},
				value(context, options) {
					const { App, System, Utils, data, promises } = this;
					const { removeSeats } = options;
					const system = System.get();

					return this.when({
						promise: promises.cart, // ensure the cart is ready before proceeding
						then: (cart) => {
							const { event, isUpgrades, purchaseFlow } = data;
							const { products } = cart;
							const removedSeatLabels = [];
							let { products: updates } = options;
							let launchPriceLevelsOverlay = false;

							if (!this.isArray(updates)) {
								// undefined, null, etc.
								updates = this.isObject(updates) ? [updates] : []; // default to empty array for undefined, null, etc.
							}

							this.each(updates, (update) => {
								const { color, id, productType, state } = update;
								const product = this.find(products, { id, productType });
								const holdId = productType === `HOLD` ? id : null;
								const holdName = productType === `HOLD` ? product.name : null;
								let { quantity, selections } = update;

								if (!this.isObject(product)) {
									// no matching sale found
									return; // skip this update
								}

								if (this.isArray(selections)) {
									if (this.isInt(quantity)) {
										if (quantity !== selections.length) {
											throw new Error(`quantity ${quantity} doesn't match selections.length ${selections.length}`);
										}
									} else {
										quantity = selections.length;
									}

									// prevent possible reference issues, e.g. the same object being pushed into selections multiple times
									selections = this.map(selections, (selection) => this.clone(selection));
								} else if (quantity === 0) {
									if (this.isArray(selections, 1)) {
										throw new Error(`quantity ${quantity} doesn't match selections.length ${selections.length}`);
									} else {
										selections = [];
									}
								}

								this.each(selections, (selection) => this.assign(selection, { holdId, holdName }));

								const { category, displayState, isLevels, isReserved, maxTicketsAllowed } = product;
								const currentSelections = this.clone(product.selections);
								const isQueued = displayState === `QUEUED`;
								let trackingAction; // used to track add/remove actions
								let selectionsDiff; // used to calculate which selections to pass to analytics

								if (this.isInt(quantity, 0)) {
									// NOTE: updates can also be updated state, color, etc.

									// populated with newly encountered selections
									const newSelections = [];
									// calculated by chipping away at the list as selections are encountered
									const removedSelections = this.clone(currentSelections);
									// save a quick-reference list of any pending selections
									const pendingSelections = this.filter(removedSelections, { state: `PENDING` });

									// iterate over the updated selections
									this.each(selections, (selection) => {
										let existingSelection;

										if (Number.isSafeInteger(selection.id)) {
											// can look up by id
											existingSelection = this.find(currentSelections, { id: selection.id });
										} else if (pendingSelections.length > 0) {
											// new selection, use any pending ones first
											existingSelection = pendingSelections.shift();
										}

										if (this.isObject(existingSelection)) {
											const allValues = this.assign({}, existingSelection, selection);
											this.assign(selection, allValues);
										} else {
											// brand new selection, assign it the default values
											this.makeProductSelection(selection);
										}

										const { id: selectionId } = selection;

										if (productType === `SALE`) {
											selection.saleId = product.id;
											selection.tierId = product.tiers[0].id;
										} else if (productType === `HOLD`) {
											if (this.isInt(selection.saleId)) {
												const sale = this.find(products, { id: selection.saleId, productType: `SALE` });
												const { tiers } = sale;

												selection.tierId = tiers[0].id;
											} else {
												selection.saleId = null;
												selection.tierId = null;
											}
										}

										if (this.isObject(existingSelection)) {
											if (
												// previously invalid, now valid
												!this.isValidSelection({ product, selection: existingSelection }) &&
												this.isValidSelection({ product, selection })
											) {
												// counts as newly inserted
												newSelections.push(selection);
											}

											// exclude from the list of removed items
											this.extractWhere(removedSelections, { id: selectionId });
										} else {
											// newly added selection
											if (this.isValidSelection({ product, selection })) {
												// should be counted for analytics
												newSelections.push(selection);
											}
										}

										// seat-related calculation
										if (isReserved && !isQueued) {
											const { state: selectionState } = selection;

											if (selectionState === `ACTIVE`) {
												if (typeof selection.seatLabel !== `string`) {
													// no seat provided, "find best available" situation
													selection.state = `FBA`;
												}
											} else if (selectionState === `PENDING`) {
												if (this.isString(selection.seatLabel, 1)) {
													// a specific seat is reserved
													selection.state = `ACTIVE`;
												}
											}
										}
									});

									if (pendingSelections.length > 0) {
										// not all pending selections were used by the update
										this.log({
											type: `warning`,
											message: `Removing leftover PENDING selections %O`,
											args: [pendingSelections],
										});

										this.each(pendingSelections, ({ id }) => {
											this.extractWhere(selections, { id });
										});
									}

									// price calculation
									this.each(selections, (selection) => {
										const { priceLevelOptionId, saleId, state: selectionState } = selection;
										const extraDetails = [];
										let priceLevelOptionName = null;
										let holdName = null;
										let holdId = null;
										let trackingId;
										let price;
										let name;

										if (productType === `SALE`) {
											holdId = null;
											name = product.name;
											trackingId = product.trackingId;

											if (isLevels) {
												if (this.isInt(priceLevelOptionId)) {
													const tier = product.tiers[0];
													const priceLevel = this.find(tier.priceLevels, { priceLevelOptionId });

													price = priceLevel.price;
													priceLevelOptionName = priceLevel.priceLevelOptionName;
													extraDetails.push(priceLevelOptionName);
												} else {
													price = product.advertisedPrice;

													if (selectionState === `ACTIVE` || selectionState === `FBA`) {
														launchPriceLevelsOverlay = true;
													}
												}
											} else {
												price = product.price;
											}
										} else if (productType === `HOLD`) {
											holdId = product.id;
											holdName = product.name;

											if (this.isInt(saleId)) {
												const saleAssignment = this.find(product.saleAssignments, { saleId });
												const sale = this.find(products, { id: saleId, productType: `SALE` });

												name = sale.name;
												trackingId = sale.trackingId;

												if (this.isString(product.name, 1)) {
													extraDetails.push(product.name);
												}

												if (isLevels) {
													if (this.isInt(priceLevelOptionId)) {
														// name calculation
														const tier = sale.tiers[0];
														const priceLevel = this.find(tier.priceLevels, { priceLevelOptionId });

														priceLevelOptionName = priceLevel.priceLevelOptionName;
														extraDetails.push(priceLevelOptionName);

														// price calculation
														const { priceLevelOptionAssignments } = saleAssignment;
														const levelAssignment = this.find(priceLevelOptionAssignments, { priceLevelOptionId });
														const priceOverride = this.isObject(levelAssignment) ? levelAssignment.price : null;

														if (priceOverride === null) {
															// no price override, rely on sale's price
															price = priceLevel.price;
														} else {
															// price override is present, use hold's override
															price = priceOverride;
														}
													} else {
														if (selectionState === `ACTIVE` || selectionState === `FBA`) {
															launchPriceLevelsOverlay = true;
														}
													}
												}

												if (this.isUndefined(price)) {
													// didn't obtain from a selected price level
													if (this.isFinite(saleAssignment.price, 0)) {
														// the sale assignment has a price override
														price = saleAssignment.price;
													} else {
														// inherit the selected sale's price
														price = sale.advertisedPrice;
													}
												}
											} else {
												// no sale selected, use default for the hold
												name = product.name;
												price = product.advertisedPrice;
											}
										}

										if (extraDetails.length > 0) {
											const detailsString = extraDetails.join(`, `);
											name += ` [${detailsString}]`;
										}

										this.assign(selection, {
											category,
											holdName,
											holdId,
											name,
											price,
											priceLevelOptionName,
											trackingId,
										});
									});

									if (this.isInt(maxTicketsAllowed)) {
										if (selections.length > maxTicketsAllowed) {
											while (selections.length > maxTicketsAllowed) {
												selections.pop();
											}

											App.notifyUser({
												message: `The maximum number of selections has been reached`,
												type: `warning`,
											});
										}
									}

									if (isUpgrades && !this.isArray(selections, 1) && this.isUpgradesReleasingSeats()) {
										selections = currentSelections;
									}

									product.selections = selections;

									const stateFilter = ({ state }) => state !== `REMOVING`;
									const visibleSelections = this.filter(selections, stateFilter);
									product.quantity = visibleSelections.length; // don't include removed selections

									if (launchPriceLevelsOverlay && !isUpgrades) {
										this.requestMissingPriceLevels();
									}

									if (newSelections.length > 0) {
										// new selections were added
										const { venue } = event;
										if (system.isMobile || venue.seatingType !== `RESERVED`) {
											App.notifyUser({
												message: `Selection${newSelections.length > 1 ? `s` : ``} added to cart`,
												type: `success`,
											});
										}

										const groupedSelections = Utils.groupBy(newSelections, (diff) => {
											const { priceLevelOptionId, saleId } = diff;
											return `${saleId}_${holdId}_${priceLevelOptionId}`;
										});

										const eventType = `cart-add`;
										this.each(groupedSelections, (grouped) => {
											App.captureInsight({ eventType, productId: id, productType, selections: grouped, purchaseFlow });
										});
									}

									if (removedSelections.length > 0) {
										// existing selections were removed
										// release temporarily held seats
										if (removeSeats) {
											const filterBySeatLabel = ({ seatLabel }) => this.isString(seatLabel, 1);
											const removedSeatLabelSelections = this.filter(removedSelections, filterBySeatLabel);

											if (removedSeatLabelSelections.length > 0) {
												// removed selections had seat labels
												const seatLabels = [];

												this.each(removedSeatLabelSelections, (removedSelection) => {
													// keep track of the seatLabel for removal
													seatLabels.push(removedSelection.seatLabel);

													// keep in the product selections array for a small buffer period
													selections.push(removedSelection);

													// remove from the list after a short buffer delay
													this.queueSelectionRemoval({
														productId: id,
														productType,
														selection: removedSelection,
													});
												});

												this.releaseSeats({ seatLabels });
											}
										}

										// analytics tracking for item removal
										const isValidFilter = (selection) => this.isValidSelection({ product, selection });
										const validRemovedSelections = this.filter(removedSelections, isValidFilter);
										const groupedSelections = Utils.groupBy(validRemovedSelections, (diff) => {
											const { priceLevelOptionId, saleId } = diff;
											return `${saleId}_${holdId}_${priceLevelOptionId}`;
										});

										const eventType = `cart-remove`;

										this.each(groupedSelections, (grouped) => {
											App.captureInsight({ eventType, productId: id, productType, selections: grouped, purchaseFlow });
										});
									}
								}

								if (this.isDefined(color)) {
									// could be null
									product.color = color;
								}

								if (this.isString(state, 1)) {
									// a state override was passed
									stateOverrides[product.trackingId] = state;
								} else if (state === null) {
									// null was passed, remove any overrides
									const override = stateOverrides[product.trackingId];

									if (this.isString(override, 1)) {
										delete stateOverrides[product.trackingId];
									}
								}

								if (trackingAction === `remove` && selectionsDiff.length > 0) {
									this.each(selectionsDiff, ({ seatLabel }) => {
										if (this.isString(seatLabel, 1)) {
											removedSeatLabels.push(seatLabel);
										}
									});
								}
							});

							this.reconcile();
						},
					});
				},
			},

			requestMissingPriceLevels: {
				async: true,
				value() {
					const { App, Events, ReservedSeating, data } = this;
					const { cart, event } = data;
					const { products, upgrades } = cart;
					const { currency, theme } = event;
					const unselectedPriceLevels = [];

					this.each(products, (product) => {
						const { displayState, isLevels, quantity, selections } = product;

						if (!isLevels || quantity === 0) {
							// not using price levels, or selections to be made
							return;
						}

						const selectableStates = [`OPEN`, `QUEUED`];
						if (!selectableStates.includes(displayState)) {
							// not in a state where selections can be made
							return;
						}

						const { id: productId, name, priceLevels, productType } = product;
						const relevantStates = [`ACTIVE`, `FBA`];
						const relevantSelectionFilter = ({ state }) => relevantStates.includes(state);
						const relevantSelections = this.filter(selections, relevantSelectionFilter);

						this.each(relevantSelections, (selection) => {
							const { id: selectionId, priceLevelOptionId, seatLabel } = selection;

							if (!this.isInt(priceLevelOptionId)) {
								const seatSummary = ReservedSeating.makeSeatSummary(selection);

								if (this.isArray(upgrades, 1)) {
									const upgrade = this.find(upgrades, (upgrade) => {
										return this.isEqual(upgrade.selection?.id, selectionId);
									});

									if (this.isObject(upgrade)) {
										const upgradesPriceLevels = priceLevels
											.map((priceLevel) => {
												const option = this.find(upgrade.targetOptions, {
													saleId: selection.saleId,
													priceLevelOptionId: priceLevel.value,
												});

												if (this.isObject(option)) {
													const formattedPriceDifference = Events.formatPriceDifference({
														priceDifference: option.priceDifference,
														currency,
													});

													return { ...priceLevel, formattedPriceDifference };
												}
											})
											.filter((priceLevel) => this.isObject(priceLevel));

										unselectedPriceLevels.push({
											name,
											priceLevels: upgradesPriceLevels,
											productId,
											productType,
											seatLabel,
											seatSummary,
											selectionId,
										});
									}
								} else {
									unselectedPriceLevels.push({
										name,
										priceLevels,
										productId,
										productType,
										seatLabel,
										seatSummary,
										selectionId,
									});
								}
							}
						});
					});

					if (unselectedPriceLevels.length === 0) {
						return;
					}

					let succeeded = false;

					// fires when the overlay is closed
					const onDie = () => {
						if (!succeeded) {
							const unselectedIds = this.map(unselectedPriceLevels, `selectionId`);
							this.removeProductSelections({ ids: unselectedIds });
						}
					};

					// fires when the overlay obtains the price level selections
					const onSuccess = (priceLevelOptionIds) => {
						// a list of price level selections was returned
						const updates = [];
						const selectionIds = [];
						this.each(unselectedPriceLevels, (unselected, i) => {
							const priceLevelOptionId = priceLevelOptionIds[i];
							const { productId, productType, selectionId } = unselected;
							const product = this.find(cart.products, { id: productId, productType });
							const { selections } = product;
							const selection = this.find(selections, { id: selectionId });
							const existing = this.find(updates, { id: productId, productType });

							selectionIds.push(selectionId);

							this.assign(selection, { priceLevelOptionId });

							if (this.isObject(existing)) {
								const quantity = selections.length;
								this.assign(existing, { quantity, selections });
							} else {
								// add to the list of updates
								const id = productId;
								updates.push({ id, productType, selections });
							}
						});

						this.updateProducts({ products: updates });
						succeeded = true;
					};

					App.loadOverlay({
						type: `SelectPriceLevels`,
						setup: {
							callbacks: {
								die: onDie,
								success: onSuccess,
							},
							theme,
							unselectedPriceLevels,
						},
					});
				},
			},

			incrementProduct: {
				public: true,
				defaults: {
					priceLevelOptionId: null,
					saleId: null,
				},
				value(context, options) {
					const { Utils, data } = this;
					const { priceLevelOptionId, id, productType } = options;
					const { cart } = data;
					const { products } = cart;
					const product = this.find(products, { id, productType });
					const { isLevels, selections: productSelections } = product;
					const selections = this.clone(productSelections);
					const { length: quantity } = selections;
					const saleId = productType === `SALE` ? id : options.saleId;
					const selection = { priceLevelOptionId, saleId };

					if (isLevels) {
						// price levels in use
						// constraints can involve splitting between 1 Adult + 1 Child = 2 tickets, etc.

						if (this.isInt(priceLevelOptionId)) {
							// price level was specified
							const priceLevel = this.find(product.priceLevels, { value: priceLevelOptionId });
							const levelSelections = this.filter(selections, { priceLevelOptionId });
							const { length: levelQuantity } = levelSelections;
							let numberToAdd = 1;

							if (productType === `SALE`) {
								// only one price level to worry about
								const { minTicketsAllowed } = priceLevel;

								if (this.isInt(minTicketsAllowed, 2) && levelQuantity < minTicketsAllowed) {
									numberToAdd = minTicketsAllowed - levelQuantity;
								}
							} else if (productType === `HOLD`) {
								// NOTE: this flow is used to insert "find best available" quantity into cart for reserved products
								// As of now, all HOLD products are reserved

								const { saleAssignments } = product;
								this.each(saleAssignments, (assignment) => {
									// traverse all sales, looking for any price levels with minimums
									const sale = this.find(products, { id: assignment.saleId, productType: `SALE` });
									const { priceLevels: salePriceLevels } = sale;
									const salePriceLevel = this.find(salePriceLevels, { value: priceLevelOptionId });
									const saleSelections = this.filter(sale.selections, { priceLevelOptionId });
									const { length: saleLevelQuantity } = saleSelections;
									const { minTicketsAllowed } = salePriceLevel;

									if (this.isInt(minTicketsAllowed, 2)) {
										const saleNumberToAdd = minTicketsAllowed - saleLevelQuantity - levelQuantity;

										if (saleNumberToAdd > numberToAdd) {
											numberToAdd = saleNumberToAdd;
										}
									}
								});
							}

							const indices = Utils.range(0, numberToAdd);
							this.each(indices, () => selections.push(selection));
						}
					}

					if (!(isLevels && this.isInt(priceLevelOptionId))) {
						const { maxTicketsAllowed, minTicketsAllowed, ticketMultiple } = product;
						let added = 0;
						let maxToAdd;

						if (!this.isInt(maxTicketsAllowed, 0)) {
							// add a ceiling to avoid an infinite loop
							maxToAdd = 1000;
						} else {
							maxToAdd = maxTicketsAllowed - quantity;
						}

						while (
							// keep incrementing while:
							added < maxToAdd &&
							(added < ticketMultiple || // fewer than ticketMultiple have been added
								selections.length % ticketMultiple !== 0 || // invalid quantity based on ticketMultiple
								selections.length < minTicketsAllowed) // fewer than minTicketsAllowed
						) {
							selections.push(selection);
							added++;
						}
					}

					this.updateProducts({
						products: [{ id, productType, selections }],
					});
				},
			},

			decrementProduct: {
				public: true,
				defaults: {
					priceLevelOptionId: null,
					saleId: null,
				},
				value(context, options) {
					const { Utils, data } = this;
					const { id, priceLevelOptionId, productType } = options;
					const { cart } = data;
					const { products } = cart;
					const product = this.find(products, { id, productType });
					const { isLevels, selections: productSelections } = product;
					const selections = this.clone(productSelections);

					if (isLevels) {
						if (this.isInt(priceLevelOptionId)) {
							// price level provided

							const priceLevel = this.find(product.priceLevels, { value: priceLevelOptionId });
							const matchingSelections = this.filter(selections, { priceLevelOptionId });
							const levelQuantity = matchingSelections.length;
							let numberToRemove = 1;

							if (productType === `SALE`) {
								const { minTicketsAllowed } = priceLevel;

								if (levelQuantity <= minTicketsAllowed) {
									numberToRemove = levelQuantity;
								}
							} else if (productType === `HOLD`) {
								// NOTE: this flow is used to remove "find best available" quantity from cart for reserved products
								// As of now, all HOLD products are reserved

								const { saleAssignments } = product;
								this.each(saleAssignments, (assignment) => {
									// traverse all sales, looking for any price levels with minimums
									const sale = this.find(products, { id: assignment.saleId, productType: `SALE` });
									const { priceLevels: salePriceLevels } = sale;
									const salePriceLevel = this.find(salePriceLevels, { value: priceLevelOptionId });
									const saleSelections = this.filter(sale.selections, { priceLevelOptionId });
									const { length: saleLevelQuantity } = saleSelections;
									const { minTicketsAllowed } = salePriceLevel;
									const totalQuantity = levelQuantity + saleLevelQuantity;

									if (totalQuantity <= minTicketsAllowed) {
										const saleNumberToRemove = levelQuantity;

										if (saleNumberToRemove > numberToRemove) {
											numberToRemove = saleNumberToRemove;
										}
									}
								});
							}

							const indices = Utils.range(0, numberToRemove);
							this.each(indices, () => this.extractWhere(selections, { priceLevelOptionId }));
						}
					}

					if (!(isLevels && this.isInt(priceLevelOptionId))) {
						const { minTicketsAllowed, ticketMultiple } = product;
						let removed = 0;

						while (
							// keep removing while:
							selections.length > 0 && // there are still items to remove
							(removed < ticketMultiple || // fewer than ticketMultiple have been removed
								selections.length % ticketMultiple !== 0 || // not a valid ticketMultiple
								selections.length < minTicketsAllowed) // jump between 0 <=> minTicketsAllowed
						) {
							selections.pop();
							removed++;
						}
					}

					this.updateProducts({
						products: [{ id, productType, selections }],
					});
				},
			},

			removeProductSelections: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { cart } = data;
					const products = this.clone(cart.products);
					const ids = this.isArray(options.ids) ? options.ids : [options.id];
					const updates = [];

					this.each(products, (product) => {
						const { id, productType, quantity, selections } = product;
						this.extract(selections, ({ id: selectionId }) => this.includes(ids, selectionId));

						if (quantity !== selections.length) {
							updates.push({ id, productType, selections });
						}
					});

					this.updateProducts({ products: updates });
				},
			},

			queueSelectionRemoval: {
				async value(options) {
					const { data } = this;
					const { cart } = data;
					const { products } = cart;
					const { productId, productType, selection } = options;
					const { id: selectionId } = selection;
					const product = this.find(products, { id: productId, productType });

					selection.state = `REMOVING`;

					await this.defer({ duration: 2000 });

					const { selections } = product;
					this.extract(selections, { id: selectionId });

					this.reconcile();
				},
			},

			releaseSeats: {
				async: true,
				async value(options) {
					const { ReservedSeating, data } = this;
					const { seatLabels } = options;

					try {
						await ReservedSeating.releaseSeats({ seatLabels });
					} catch (error) {
						// assume success, swallow error
					}

					data.releasingSeats = false;
					this.reconcile();
				},
			},

			fetch: {
				async: true,
				defaults: {
					ignoreErrors: true,
				},
				value(options) {
					const { XHR } = this;
					const { eventId, ignoreErrors, token } = options;
					const url = `/api/events/${eventId}/shoppingcarts/${token}`;
					const method = `GET`;

					return XHR.ajax({ ignoreErrors, method, url });
				},
			},

			get: {
				async: false, // needs raw access to async options below
				public: true,
				defaults: {
					subscribe: false,
				},
				value(context, options) {
					const { data } = this;

					if (options.subscribe) {
						// keep track of the handler functions if this is a subscription
						this.subscribe({
							owner: context,
							then: options.then,
							fail: options.fail,
						});
					}

					options.owner = context;
					options.promise = this.clone(data.cart);
					return this.when(options);
				},
			},

			subscribe: {
				public: true,
				value(context, options) {
					options = this.defaults(options, {
						owner: context,
					});

					this.subscriptions.push({
						handler: options.then,
						errorHandler: options.fail,
						owner: options.owner,
					});
				},
			},

			checkout: {
				clone: true,
				public: true,
				defaults: {},
				value(context, options) {
					const { App, data } = this;
					const { cart, event, groupSaleId, groupSaleUrlParam, isUpgrades, purchaseFlow } = data;
					const { id: eventId } = event;
					const { trt } = options;
					const paymentPlanFee = this.isObject(cart) && cart.usePaymentPlan ? cart.paymentPlanFee : null;
					const orderProtectionFee =
						this.isObject(cart) && cart.orderProtectionAdded ? cart.orderProtection.quote : null;

					let promise;

					this.defaults(options, {
						owner: context,
					});

					data.checkoutActive = true;

					if (event.captcha) {
						promise = this.require({
							deps: [`Captcha`],
							then: () => {
								const { Captcha } = this;
								const path = App.getPath();

								App.captureInsight({
									eventId,
									eventType: `page-view`,
									pageType: `Checkout`,
									path: `${path}/captcha`,
									title: `Event Page, Captcha (${event.id}: ${event.name})`,
									cart,
									purchaseFlow,
									paymentPlanFee,
									orderProtectionFee,
								});

								return Captcha.getResponseToken();
							},
						});
					} else {
						promise = DUMMY_RECAPTCHA;
					}

					promise = this.when({
						promise,
						then: (recaptchaResponse) => {
							const { cart } = this.data;
							const reserveOptions = { recaptchaResponse };

							if (this.isArray(cart.saleItems, 1) && !cart.completed) {
								// saved cart; return without reserving
								return cart;
							}

							if (this.isString(trt)) {
								reserveOptions.trt = trt;
							}

							return this.reserve(reserveOptions);
						},
					});

					promise = this.when({
						promise,
						then: () => {
							const hasAddOns =
								this.isArray(cart.add_ons, 1) || this.isObject(this.find(cart.products, { type: `ADD_ON` }));

							if (hasAddOns && cart.showAddOns !== false) {
								const deferred = this.Deferred();

								App.loadOverlay({
									type: `AddOnsPicker`,
									setup: {
										theme: event.theme,
										eventId: cart.eventId,
										callbacks: {
											success: deferred.resolve, // skipped or added tickets, continue
											die: () => {
												deferred.reject({
													message: `ShoppingCart: User exited AddOnsPicker`,
													suppress: true,
												});
											},
										},
									},
									fail: deferred.reject,
								});

								return deferred.promise;
							}
						},
					});

					promise = this.when({
						promise,
						then: () => {
							// add-ons stage complete
							var deferred;

							if (cart.requirements.q_config && cart.showWaitlist !== false) {
								// this is a 'queue' order
								deferred = this.Deferred();

								App.loadOverlay({
									// launch the overlay
									type: `CheckoutQueueDetails`,
									defer: false, // show immediately
									replace: false, // keep the current overlay active
									setup: {
										theme: event.theme,
										callbacks: {
											success: deferred.resolve,
											die: () => {
												deferred.reject({
													message: `ShoppingCart: User exited CheckoutQueueDetails`,
													suppress: true,
												});
											},
										},
									},
									then: () => {
										const path = App.getPath();

										App.captureInsight({
											// log the 'pageview' of the disclaimer overlay
											eventId,
											eventType: `page-view`,
											pageType: `Checkout`,
											path: `${path}/queue/agreement`,
											title: `Queue, agreement (${event.id}: ${event.name})`,
											cart,
											purchaseFlow,
											paymentPlanFee,
											orderProtectionFee,
										});
									},
									fail: deferred.reject,
								});

								return deferred.promise;
							}
						},
					});

					promise = this.when({
						promise,
						then: () => {
							// add-ons complete
							var deferred, agreed;

							if (cart.requirements.payment_plan) {
								deferred = this.Deferred();

								if (this.isObject(cart.paymentSchedule)) {
									App.loadOverlay({
										type: `PaymentSchedule`,
										replace: false, // keep the existing overlay(s) running
										defer: false, // show the overlay immediately
										setup: {
											theme: `DARK`,
											contentMode: `require`,
											paymentSchedule: cart.paymentSchedule,
											currency: event.currency,
											timezone: event.venue.timezone,
											eventId: event.id,
											callbacks: {
												success: () => {
													agreed = true;
												},
												die: () => {
													if (agreed === true) {
														this.enablePaymentPlan({
															then: deferred.resolve,
															fail: deferred.reject,
														});
													} else {
														deferred.reject({
															message: `ShoppingCart: User exited required PaymentSchedule`,
															suppress: true,
														});
													}
												},
											},
										},
										fail: deferred.reject,
									});
								} else {
									if (cart.features.payment_plan_optional) {
										// allowed to skip this step
										deferred.resolve();
									} else {
										// no way to proceed, error out
										deferred.reject({
											message: `We're sorry, payment plans are no longer available.`,
										});
									}
								}

								return deferred.promise;
							} else if (cart.useCustomPaymentSchedule) {
								deferred = this.Deferred();

								// Custom payments flow
								const paymentSchedule = {
									installment: this.map(cart.customPaymentSchedule.scheduledPayments, (payment) => {
										return {
											paymentAmount: payment.amount,
											status: payment.status,
											paymentDate: payment.paymentDate ? payment.paymentDate : payment.scheduledDate,
											issuanceConditions: payment.issuanceConditions,
											token: payment.token,
										};
									}),
									currentPayment: cart.currentPayment,
									terms: cart.customPaymentSchedule.terms,
									isCustomPaymentSchedule: true,
								};

								this.App.loadOverlay({
									type: `PaymentSchedule`,
									replace: false, // keep the existing overlay(s) running
									defer: false, // show immediately
									setup: {
										theme: `DARK`,
										contentMode: `status`,
										paymentSchedule,
										currency: event.currency,
										timezone: event.venue.timezone,
										eventId: event.id,
										callbacks: {
											success: () => {
												agreed = true;
											},
											die: () => {
												if (agreed === true) {
													deferred.resolve();
												} else {
													if (cart.useCustomPaymentSchedule) {
														window.history.back();
													} else {
														deferred.reject({
															message: `ShoppingCart: User exited PaymentSchedule overview`,
															suppress: true,
														});
													}
												}
											},
										},
									},
									fail: deferred.reject,
								});

								return deferred.promise;
							}
						},
					});

					promise = this.when({
						promise, // reserved successfully; NOTE: should only fire once per potential order
						then: () =>
							App.loadOverlay({
								type: `Checkout`,
								replace: true,
								setup: {
									theme: `LIGHT`,
									backgroundColor: `#F5F5F5`, // hack for mobile when the form is pulled up a bit so it looks like it's part of the form
									backgroundOpacity: 1,
									isUpgrades,
									purchaseFlow,
									callbacks: {
										die: () => {
											if (this.isString(groupSaleUrlParam, 1) && !cart.completed) {
												this.navigateToOriginalGroupSale({ groupSaleId, groupSaleUrlParam });
											}
										},
									},
								},
							}),
					});

					options.promise = promise;
					promise = this.when(options); // run through any passed handlers

					return this.when({
						promise,
						then: () => {
							this.reconcile();
							return cart;
						},
						fail: (reason) => {
							this.init(); // reset the cart if the checkout fails (e.g. from deferred.reject() calls from modals)

							if (!this.isObject(reason) || reason.suppress !== true) {
								throw reason; // re-throw the error
							}
						},
						fin: () => (data.checkoutActive = false),
					});
				},
			},

			checkoutUpgrades: {
				clone: true,
				public: true,
				defaults: {},
				value(context, options) {
					const { App, data } = this;
					const { cart, event, isUpgrades, purchaseFlow } = data;
					const paymentPlanFee = this.isObject(cart) && cart.usePaymentPlan ? cart.paymentPlanFee : null;
					const orderProtectionFee =
						this.isObject(cart) && cart.orderProtectionAdded ? cart.orderProtection.quote : null;
					const { trt } = options;
					let promise;

					this.defaults(options, {
						owner: context,
					});

					data.checkoutActive = true;

					if (event.captcha) {
						promise = this.require({
							deps: [`Captcha`],
							then: () => {
								const { Captcha } = this;
								const path = App.getPath();

								App.captureInsight({
									eventType: `page-view`,
									pageType: `Checkout`,
									path: `${path}/captcha`,
									title: `Event Page, Captcha (${event.id}: ${event.name})`,
									cart,
									purchaseFlow,
									paymentPlanFee,
									orderProtectionFee,
								});

								return Captcha.getResponseToken();
							},
						});
					} else {
						promise = DUMMY_RECAPTCHA;
					}

					promise = this.when({
						promise,
						then: (recaptchaResponse) => {
							const reserveOptions = { recaptchaResponse };

							if (this.isString(trt)) {
								reserveOptions.trt = trt;
							}

							reserveOptions.upgrades = true;

							return this.reserve(reserveOptions);
						},
					});

					/* removed steps that are not needed for upgrades
					 ** add-ons
					 ** queue
					 */

					promise = this.when({
						promise,
						then: () => {
							var deferred, agreed;

							if (cart.requirements.payment_plan) {
								deferred = this.Deferred();

								if (this.isObject(cart.paymentSchedule)) {
									App.loadOverlay({
										type: `PaymentSchedule`,
										replace: false, // keep the existing overlay(s) running
										defer: false, // show the overlay immediately
										setup: {
											contentMode: `require`,
											paymentSchedule: cart.paymentSchedule,
											currency: event.currency,
											timezone: event.venue.timezone,
											callbacks: {
												success: () => {
													agreed = true;
												},
												die: () => {
													if (agreed === true) {
														this.enablePaymentPlan({
															then: deferred.resolve,
															fail: deferred.reject,
														});
													} else {
														deferred.reject({
															message: `ShoppingCart: User exited required PaymentSchedule`,
															suppress: true,
														});
													}
												},
											},
										},
										fail: deferred.reject,
									});
								} else {
									if (cart.features.payment_plan_optional) {
										// allowed to skip this step
										deferred.resolve();
									} else {
										// no way to proceed, error out
										deferred.reject({
											message: `We're sorry, payment plans are no longer available.`,
										});
									}
								}

								return deferred.promise;
							}
						},
					});

					promise = this.when({
						promise, // reserved successfully; NOTE: should only fire once per potential order
						then: () =>
							App.loadOverlay({
								type: `Checkout`,
								replace: true,
								setup: {
									theme: `LIGHT`,
									backgroundColor: `#F5F5F5`, // hack for mobile when the form is pulled up a bit so it looks like it's part of the form
									backgroundOpacity: 1,
									isUpgrades,
									purchaseFlow,
								},
							}),
					});

					options.promise = promise;
					promise = this.when(options); // run through any passed handlers

					return this.when({
						promise,
						then: () => {
							this.reconcile();
							return cart;
						},
						fail: (reason) => {
							this.leaveUpgradesMobileWebview();
							// reset the cart
							this.init();

							if (!this.isObject(reason) || reason.suppress !== true) {
								throw reason; // re-throw the error
							}
						},
						fin: () => (data.checkoutActive = false),
					});
				},
			},

			reserve: {
				async: true,
				defaults: {
					recaptchaResponse: null,
				},
				value(options) {
					// make the initial request to reserve a token
					const { App, Locale, ReservedSeating, System, User, Utils, XHR, data } = this;
					const { cart, event, purchaseFlow } = data;
					const { referrer } = document;
					const { recaptchaResponse, trt, upgrades } = options;
					const { currency, groupSaleToken, privateLinkToken } = cart;
					const reservedSeating = ReservedSeating.get();
					const accessCodes = this.getAccessCodes();
					const system = System.get();
					const localeInfo = Locale.get();
					const { selected: selectedLocale } = localeInfo;
					const { seatingChartHoldId } = reservedSeating;
					const { key: locale } = selectedLocale;
					const { isBrowser } = system;
					let refId = App.getUrlParam(`r`);
					let canceled = false;
					let waitingRoom;
					let onCancel;
					let promise;
					let body;

					body = {
						accessCodes,
						currency,
						recaptchaResponse,
						referrer,
						refId,
						locale,
					};

					if (this.isString(trt, 1)) {
						// resuming has confused fans when clicking multiple email links in a row
						data.enablePersistentCheckout = false;

						body.trt = trt;
					} else {
						const saleItems = [];
						let hasReserved = false;
						let hasDynamicQueued = false;

						// avoid possible sync issues when embedded in native apps
						data.enablePersistentCheckout = isBrowser;

						body.saleItems = saleItems;
						this.each(cart.products, (product) => {
							const { displayState, id, isLevels, isStateOverride, isReserved, productType, quantity } = product;
							const isQueued = displayState === `QUEUED`;
							let isDynamicQueued = false;

							if (quantity > 0) {
								// product will be included in request
								if (isQueued) {
									if (!hasDynamicQueued && isStateOverride) {
										// client-side dynamic waitlist was used, keep track for cart request body
										hasDynamicQueued = true;
										isDynamicQueued = true;
									}
								} else if (isReserved && !hasReserved) {
									// keep track for cart request body
									hasReserved = true;
								}
							}

							if (productType === `SALE`) {
								// less complex, no potential for splitting up
								const { deliveryConfiguration, quantity, tiers } = product;
								const { id: tierId } = tiers[0];
								let additionalDeliveryType;
								let deliveryType;

								if (this.isObject(deliveryConfiguration)) {
									deliveryType = deliveryConfiguration.deliveryType;
									additionalDeliveryType = deliveryConfiguration.additionalDeliveryType;
								} else {
									deliveryType = null;
									additionalDeliveryType = null;
								}

								if (quantity > 0) {
									if (isLevels) {
										// price levels, potentially have to split into multiple saleItems
										const { priceLevels } = product;

										this.each(priceLevels, ({ value: priceLevelOptionId }) => {
											const levelSelections = this.filter(product.selections, { priceLevelOptionId });

											if (levelSelections.length > 0) {
												let items;

												if (isReserved && !isQueued) {
													items = this.map(levelSelections, ({ seatLabel }) => ({ seatLabel }));
												} else {
													items = null;
												}

												const saleItem = {
													additionalDeliveryType,
													deliveryType,
													holdId: null,
													items,
													priceLevelOptionId,
													quantity: levelSelections.length,
													saleId: id,
													tierId,
												};

												if (isDynamicQueued) {
													saleItem.q = true;
												}

												saleItems.push(saleItem);
											}
										});
									} else {
										// normal pricing, single saleItem
										let items;

										if (isReserved && product.displayState !== `QUEUED`) {
											items = this.map(product.selections, ({ seatLabel }) => ({ seatLabel }));
										} else {
											items = null;
										}

										const saleItem = {
											additionalDeliveryType,
											deliveryType,
											holdId: null,
											items,
											priceLevelOptionId: null,
											quantity,
											saleId: id,
											tierId,
										};

										if (isDynamicQueued) {
											saleItem.q = true;
										}

										saleItems.push(saleItem);
									}
								}
							} else if (productType === `HOLD`) {
								const allSaleIds = this.map(product.selections, `saleId`);
								const saleIds = this.unique(allSaleIds);

								this.each(saleIds, (saleId) => {
									const included = this.filter(product.selections, { saleId });
									const quantity = included.length;

									if (quantity > 0) {
										const assignment = this.find(product.saleAssignments, { saleId });
										const { deliveryConfiguration } = assignment;
										const sale = this.find(cart.products, { id: saleId, productType: `SALE` });
										const { id: tierId } = sale.tiers[0];
										let additionalDeliveryType;
										let deliveryType;

										if (this.isObject(deliveryConfiguration)) {
											additionalDeliveryType = deliveryConfiguration.additionalDeliveryType;
											deliveryType = deliveryConfiguration.deliveryType;
										} else {
											additionalDeliveryType = null;
											deliveryType = null;
										}

										if (isLevels) {
											// price levels, potentially have to split into multiple saleItems
											const { priceLevels } = product;

											this.each(priceLevels, ({ value: priceLevelOptionId }) => {
												const levelSelections = this.filter(included, { priceLevelOptionId });

												if (levelSelections.length > 0) {
													let items;

													if (isReserved && product.displayState !== `QUEUED`) {
														items = this.map(levelSelections, ({ seatLabel }) => ({ seatLabel }));
													} else {
														items = null;
													}

													const saleItem = {
														additionalDeliveryType,
														deliveryType,
														holdId: id,
														items,
														priceLevelOptionId,
														quantity: levelSelections.length,
														saleId,
														tierId,
													};

													if (isDynamicQueued) {
														saleItem.q = true;
													}

													saleItems.push(saleItem);
												}
											});
										} else {
											// normal pricing, single saleItem
											let items;

											if (isReserved && product.displayState !== `QUEUED`) {
												items = this.map(included, ({ seatLabel }) => ({ seatLabel }));
											} else {
												items = null;
											}

											const saleItem = {
												additionalDeliveryType,
												deliveryType,
												holdId: id,
												items,
												priceLevelOptionId: null,
												quantity,
												saleId,
												tierId,
											};

											if (isDynamicQueued) {
												saleItem.q = true;
											}

											saleItems.push(saleItem);
										}
									}
								});
							}
						});

						if (hasDynamicQueued) {
							body.q = true;
						}

						if (this.isString(seatingChartHoldId)) {
							if (hasReserved) {
								body.seatingChartHoldId = seatingChartHoldId;
							} else {
								// moving into checkout with no reserved items, end the seats.io session
								ReservedSeating.endSession({ deleteHold: true });
							}
						} else if (hasReserved) {
							throw new Error(`Seating chart session is required but not found`);
						}
					}

					if (this.isString(privateLinkToken, 1)) {
						body.privateLinkToken = privateLinkToken;
					}

					if (this.isString(groupSaleToken, 1)) {
						body.groupSaleToken = groupSaleToken;
					}

					if (upgrades) {
						body.upgradeInfo = {
							sourceOrderId: cart.orderId,
						};

						this.each(body.saleItems, (saleItem) => {
							saleItem.upgradeInfo = {
								upgradeSourceItemInfos: [],
							};

							if (this.isArray(saleItem.items, 1)) {
								this.each(saleItem.items, (item) => {
									const { seatLabel } = item;
									const matchingUpgrade = this.find(cart.upgrades, (upgrade) => {
										if (upgrade.selection.seatLabel === seatLabel) {
											const sourceAlreadyAdded = this.find(saleItem.upgradeInfo.upgradeSourceItemInfos, {
												sourceSaleOrderItemId: upgrade.source,
											});

											if (!this.isObject(sourceAlreadyAdded)) {
												return true;
											}
										}

										return false;
									});

									if (this.isObject(matchingUpgrade)) {
										const { source, targetSelected = {} } = matchingUpgrade;
										const { priceListId } = targetSelected;

										saleItem.upgradeInfo.upgradeSourceItemInfos.push({
											sourceSaleOrderItemId: source,
											targetSeatLabel: seatLabel,
										});

										// attach any priceListId that was on the target selection
										item.priceListId = this.isInt(priceListId) ? priceListId : null;
									}
								});
							} else {
								this.each(cart.upgrades, (upgrade) => {
									const { source, targetSelected } = upgrade;
									const { saleId, tierId, /*holdId,*/ priceLevelOptionId, priceListId } = targetSelected;

									if (
										saleId === saleItem.saleId &&
										tierId === saleItem.tierId &&
										priceLevelOptionId == saleItem.priceLevelOptionId
									) {
										saleItem.priceListId = priceListId;
										saleItem.upgradeInfo.upgradeSourceItemInfos.push({ sourceSaleOrderItemId: source });
									}
								});
							}
						});

						// TODO: replace with something better integrated
						const updatedSaleItems = [];
						this.each(body.saleItems, (saleItem) => {
							if (this.isArray(saleItem.items, 1)) {
								const { items, upgradeInfo } = saleItem;
								const { upgradeSourceItemInfos } = upgradeInfo;
								const groupedUpdatedSourceItemInfos = {};
								let i = 0; // NOTE: groupBy() doesn't provide a second index argument, manually define and increment
								const groupedItemLists = Utils.groupBy(items, (item) => {
									const { priceListId } = item; // will be used as the key for grouping
									const matchingSourceItemInfo = upgradeSourceItemInfos[i];
									const priceListIdString = `${priceListId}`;

									// group the associated itemInfo values by the same key
									if (this.isArray(groupedUpdatedSourceItemInfos[priceListIdString])) {
										// array already exists, push into it
										groupedUpdatedSourceItemInfos[priceListIdString].push(matchingSourceItemInfo);
									} else {
										// create new array to hold the itemInfo values for this key
										groupedUpdatedSourceItemInfos[priceListIdString] = [matchingSourceItemInfo];
									}

									i++; // increment the counter

									// group the items by the key
									return priceListIdString;
								});

								this.each(groupedItemLists, (groupedItems, priceListIdString) => {
									// parse the priceListId from the group's key
									const priceListIdNumber = this.parseInt(priceListIdString);
									const priceListId = this.isInt(priceListIdNumber) ? priceListIdNumber : null;

									// clone the existing saleItem, to augment and add to updatedSaleItems
									const saleItemCopy = this.clone(saleItem);
									// fetch the array of upgradeSourceItemInfos that was accumulated earlier
									const matchingSourceItemInfo = groupedUpdatedSourceItemInfos[priceListIdString];

									// set the priceListId at the saleItem
									saleItemCopy.priceListId = priceListId;

									// clear the priceLevelId at the item level
									this.each(groupedItems, (groupedItem) => delete groupedItem.priceListId);

									// use the subset of items grouped by key
									saleItemCopy.items = groupedItems;
									// use the subset of upgradeSourceItemInfos grouped by key
									saleItemCopy.upgradeInfo.upgradeSourceItemInfos = matchingSourceItemInfo;
									// push into the new array of saleItems
									updatedSaleItems.push(saleItemCopy);
								});
							} else {
								// no items array to potentially split, copy over as-is
								updatedSaleItems.push(saleItem);
							}
						});

						body.saleItems = updatedSaleItems;
					}

					promise = XHR.ajax({
						block: false,
						type: `POST`,
						REST: true,
						url: `/api/events/${event.id}/shoppingcarts`,
						body,
						then: (reservation) => {
							// initial response received
							let requestId;

							function placeRequest() {
								// used in the case of a deferred call, to ping again for status
								body.requestId = requestId;
								body.recaptchaResponse = DUMMY_RECAPTCHA;

								if (!canceled) {
									return XHR.ajax({
										// repeat the same call, with requestId attached
										block: false,
										type: `POST`,
										REST: true,
										url: `/api/events/${event.id}/shoppingcarts`,
										body,
										then: onResponse,
									});
								}
							}

							function onResponse(response) {
								// used every time a response is received
								if (this.isString(response.token, 1)) {
									// a valid token was received, reservation successful
									if (response.authenticated) {
										if (response.guestCheckoutBehavior === `AGENT_ONLY`) {
											// forced guest checkout, log
											User.logOut();
										}
									} else {
										// user isn't authenticated
										User.set({
											// log them out locally
											value: null,
										});
									}

									cart.token = response.token;
									cart.upgradeSubTotal = response.upgradeSubTotal;

									return this.syncCart({
										response,
										then: (result) => {
											App.captureInsight({ eventType: `checkout-init`, purchaseFlow });
										},
									});
								} else if (this.isObject(response.delay)) {
									// this is an async request
									const { delay } = response;
									const { cb, warning: message } = delay;
									const duration = this.isInt(cb) ? cb * 1000 : 1000; // use callback delay from server if available

									requestId = delay.requestId;

									if (this.isString(message, 1)) {
										if (this.isObject(waitingRoom) && this.isFunction(waitingRoom.update)) {
											waitingRoom.update({ message });
										}
									}

									return this.defer({
										duration, // wait the requested amount of time
										then: placeRequest, // poll for status
									});
								} else if (response.alert === true) {
									App.loadOverlay({
										type: `Message`,
										setup: {
											type: `alert`,
											message: response.warning,
										},
									});

									throw {
										message: response.warning,
										suppress: true,
									};
								} else {
									this.log({
										type: `warning`,
										message: `No token or requestId present on ticket reservation`,
									});

									throw {
										message: `There was an error reserving tickets`,
									};
								}
							}

							return onResponse.call(this, reservation);
						},
					});

					// callback if the user exits the waiting room
					onCancel = () => (canceled = true);

					promise = this.when({
						promise,
						fail: (reason) => {
							// failed to obtain a shopping cart token, seating chart session is invalidated
							ReservedSeating.endSession({ deleteHold: true });
							throw reason;
						},
					});

					waitingRoom = App.useWaitingRoom({ onCancel, promise });
					return waitingRoom.promise;
				},
			},

			updateQueueSettings: {
				async: true,
				public: true,
				value(context, options) {
					const data = this.data;
					const cart = data.cart;
					const event = data.event;

					return this.XHR.ajax({
						type: `PUT`,
						REST: true,
						url: `/api/events/${event.id}/shoppingcarts/${cart.token}?action=change-queue-settings`,
						data: {
							queue_settings: options.data,
						},
						then: (response) => {
							cart.progress.q_config = true;

							return this.syncCart({
								response,
								then: () => response,
							});
						},
					});
				},
			},

			updateShippingSettings: {
				async: true,
				public: true,
				value(context, { shippingAddress }) {
					const data = this.data;
					const cart = data.cart;
					const event = data.event;
					const url = `/api/events/${event.id}/shoppingcarts/${cart.token}?action=change-delivery`;
					const saleItems = this.map(cart.saleItems, (saleItem) => {
						const { holdId, priceLevelId, priceLevelOptionId, priceListId, quantity, saleId, tierId } = saleItem;
						const sale = this.find(cart.products, { id: saleId, productType: `SALE` });
						const productType = this.isInt(holdId) ? `HOLD` : `SALE`;
						const itemBody = { holdId, priceLevelId, priceLevelOptionId, priceListId, quantity, saleId, tierId };
						let additionalDeliveryType;
						let deliveryConfiguration;
						let deliveryType;

						if (productType === `SALE`) {
							deliveryConfiguration = sale.deliveryConfiguration;
						} else if (productType === `HOLD`) {
							const hold = this.find(cart.products, { id: holdId, productType: `HOLD` });
							const assignment = this.find(hold.saleAssignments, { saleId });
							deliveryConfiguration = assignment.deliveryConfiguration;
						}

						deliveryType = deliveryConfiguration.deliveryType;
						additionalDeliveryType = deliveryConfiguration.additionalDeliveryType;

						if (this.isNull(shippingAddress)) {
							// will-call substitution
							if (deliveryType === `SHIPPING`) {
								deliveryType = `WILLCALL`;
							}
							if (additionalDeliveryType === `SHIPPING`) {
								additionalDeliveryType = `WILLCALL`;
							}
						}

						this.assign(itemBody, { additionalDeliveryType, deliveryType });
						return itemBody;
					});
					const body = { saleItems, shippingAddress };
					const type = `PUT`;
					const REST = true;
					const then = (response) => {
						// update internal values from the latest response, and notify subscribers
						cart.shippingAddress = shippingAddress;
						cart.progress.shipping_address = true;

						return this.syncCart({
							response,
							then: () => response,
						});
					};

					return this.XHR.ajax({ body, REST, then, type, url });
				},
			},

			updateLocaleSetting: {
				async: true,
				value() {
					const { data } = this;
					const { cart, event, selectedLocale } = data;

					if (
						this.isString(selectedLocale?.key, 1) &&
						this.isString(cart?.token, 1) &&
						this.isObject(event) &&
						!cart.completed
					) {
						return this.XHR.ajax({
							body: { key: selectedLocale.key },
							REST: true,
							type: `PUT`,
							url: `/api/events/${event.id}/shoppingcarts/${cart.token}?action=change-locale`,
						});
					}
				},
			},

			revisitShipping: {
				public: true,
				value(context) {
					const { data } = this;
					const { cart } = data;

					if (!cart.requirements.shipping_address) {
						throw new Error(`Shipping address is not required`);
					}

					cart.progress.shipping_address = false;
					this.reconcile();
				},
			},

			unsubscribe: {
				public: true,
				defaults: {},
				value(context, options) {
					this.defaults(options, {
						owner: context,
					});

					this.extract(this.subscriptions, options);
				},
			},

			loadForms: {
				public: true,
				async: true,
				defaults: {
					required: true,
				},
				value(context, options) {
					const { App, Deferred, data } = this;
					const { required } = options;
					const { cart, custom_forms, event, purchaseFlow, user, questionsLoaded = false } = data;
					const { expiration, hasBatchSubmissions, saleItems, token } = cart;
					const { id: eventId, theme } = event;
					const forms = this.extract(custom_forms, { required });

					if (this.isObject(user) && forms.length > 0 && !hasBatchSubmissions && !questionsLoaded) {
						const reservedSaleItem = this.find(saleItems, ({ cartedSaleItems }) => {
							return this.find(cartedSaleItems, ({ seatLabel }) => this.isString(seatLabel, 1));
						});
						const cartHasReservedSaleItems = this.isObject(reservedSaleItem);

						let filteredForms = [];

						if (cartHasReservedSaleItems) {
							// cart has at least one seatLabel value, include SEAT_SELECTION questions
							filteredForms = forms;
						} else {
							// no seatLabels present in the cart, filter out SEAT_SELECTION questions
							this.each(forms, (form) => {
								const filteredForm = this.clone(form);
								const { questions } = form;
								const filteredQuestions = this.filter(questions, ({ type }) => type !== `SEAT_SELECTION`);
								filteredForm.questions = filteredQuestions;

								if (filteredQuestions.length > 0) {
									filteredForms.push(filteredForm);
								}
							});

							if (filteredForms.length === 0) {
								// no forms to show
								return;
							}
						}

						// user logged in and forms are present
						const deferred = Deferred();
						const action = required ? `checkout-required` : `checkout-optional`;
						const setup = {
							theme,
							forms: filteredForms,
							action,
							eventId,
							token,
							expiration,
							required,
							callbacks: {
								success: () => {
									const eventType = `checkout-step-complete`;
									const step = `custom-forms`;
									App.captureInsight({ eventType, step, purchaseFlow });

									data.questionsLoaded = false;

									deferred.resolve();
								},
							},
						};

						if (required) {
							setup.callbacks.die = () => {
								if (!deferred.promise.isFulfilled()) {
									// still pending or rejected
									App.notifyUser({
										type: `error`,
										message: `Answers are required to continue`,
									});

									this.init(); // error'd out, do a light cart reset

									data.questionsLoaded = false;

									deferred.reject(new Error(`ShoppingCart: Couldn't obtain required form answers`)); // notify the original caller
								}
							};
						} else {
							setup.callbacks.die = () => {
								data.questionsLoaded = false;
								deferred.resolve; // no success, but not required, resolve
							};
						}

						if (this.isDefined(cart.orderId)) {
							setup.orderId = cart.orderId;
						}

						App.loadOverlay({
							type: `Questions`,
							defer: false, // show the overlay immediately
							replace: false, // keep any existing overlay in the background
							setup,
						});

						data.questionsLoaded = true;

						const eventType = `checkout-step`;
						const step = `custom-forms`;
						App.captureInsight({ eventType, step });

						return deferred.promise;
					}
				},
			},

			/**
			 * Ask the user if they want to apply a promo code that was entered but not applied
			 */
			checkPromoCode: {
				public: true,
				async: true,
				value(context, options) {
					const { data, App } = this;
					const { cart, incomplete_promo } = data;

					if (!this.isString(cart.promoCode, 1) && this.isString(incomplete_promo, 1)) {
						return App.askUser({
							message: `You entered a promo code but did not apply it. Would you like to apply it?`,
							then: (answer) => {
								if (answer) {
									return this.addPromo({
										force: true, // override the fact that submission has started
										code: incomplete_promo,
										then: () => {
											throw {
												docType: `CANCEL`,
											};
										},
										fail: (reason) => {
											throw {
												docType: `CANCEL`,
											};
										},
									});
								}
							},
						});
					}
				},
			},

			submit: {
				public: true,
				async: true,
				defaults: {
					useAsDefault: true,
					savePaymentMethod: true,
				},
				value(context, options) {
					const { App, Config, Payment, Time, data } = this;
					const { EXTERNAL_TENDER_TYPES } = Config;
					const { cart, event, user } = data;
					const { agentCheckoutFanId, expiration, requirements } = cart;
					const { paymentProcessorType } = event;
					const currentTime = Time.getTime();
					const key = `buy`;
					let { tenderType } = options;
					const isExternalPayment = this.includes(EXTERNAL_TENDER_TYPES, tenderType);
					if (buyRequestPlaced) {
						throw new Error(`ShoppingCart.submit() already running`);
					}
					if (currentTime > expiration) {
						// cart has expired
						return this.expireCart();
					}
					buyRequestPlaced = true;

					cart.useAsDefault = options.useAsDefault;
					cart.savePaymentMethod = options.savePaymentMethod;
					cart.paymentMethodToken = null;
					cart.billingAddress = options.billingAddress;
					cart.tenderType = null;
					cart.nonce = null;

					App.block({ key });

					if (!requirements.payment) {
						tenderType = `NONE`;
					}

					if (!requirements.payment || paymentProcessorType !== `STRIPE` || isExternalPayment) {
						// BRAINTREE FLOW, No Payment (waitlist, free), External Payment
						this.callSubmitCartAction(options);
					} else {
						// STRIPE CHECKOUT FLOW
						let promise;

						if (cart.savePaymentMethod && requirements.q_config) {
							// Waitlist
							if (options.token) {
								// existing payment method
								options.methodToken = options.token;
								this.callSubmitCartAction(options);
							} else {
								// new payment method
								promise = Payment.confirmSetupIntent({
									shippingAddress: cart.shippingAddress,
									addressMatch: cart.requirements.address_match,
								}).then((setupIntent) => {
									options.methodToken = setupIntent.payment_method;
									this.callSubmitCartAction(options);
								});
							}
						} else {
							if (options.token) {
								// existing payment method
								promise = Promise.resolve({ payment_method: options.token });
							} else {
								// new payment method
								promise = Payment.confirmSetupIntent({
									shippingAddress: cart.shippingAddress,
									addressMatch: cart.requirements.address_match,
								});
							}

							promise = this.when({
								promise,
								then: ({ payment_method: token }) => {
									let payload = {
										groupId: event.group.id,
										token, //paymentToken,
										then: (result) => result.id, // pass through the translated token id from the response
									};

									// Invoices
									if (agentCheckoutFanId) {
										payload.userId = agentCheckoutFanId;
									}

									return Payment.translateMethod(payload);
								},
							});

							promise = this.when({
								promise,
								then: (paymentToken) => {
									// translated token is ready
									return Payment.confirmIntent({
										payment_method: paymentToken,
										cvcToken: options.cvcToken ? options.cvcToken.id : undefined,
										type: `payment`,
										paymentIntent: cart.paymentIntent,
										userId: user ? user.id : agentCheckoutFanId,
										shippingAddress: cart.shippingAddress,
										addressMatch: cart.requirements.address_match,
										then: async (stripeResult) => {
											const confirmation = await this.pingForStripeOrderResult();
											this.onOrderComplete({ confirmation });
										},
										fail: (error) => {
											this.resetPaymentMethod({ value: true });
											throw error;
										},
									});
								},
							});
						}

						return this.when({
							promise,
							fail: (error) => {
								App.notifyUser({
									type: `error`,
									message: error.message,
								});
							},
							fin: () => {
								buyRequestPlaced = false;
								App.unblock({ key });
							},
						});
					}
				},
			},

			/**
			 * Reset the payment method form in Checkout steps
			 */
			resetPaymentMethod: {
				public: true,
				value(context, { value }) {
					this.data.cart.resetPaymentMethod = value;
					this.reconcile();
				},
			},

			/**
			 * Ping the BE to see if the order has been completed and launch the OrderComplete overlay. This gets called from Event.js when a token is present in the URL.
			 */
			checkOrderComplete: {
				public: true,
				value(context, options) {
					const { App } = this;
					const { data } = this;
					const { cart, purchaseFlow } = data;
					const { token } = options;

					App.block({ key: `checkOrderComplete` });

					this.when({
						promise: this.pingForStripeOrderResult({ token }),
						then: (confirmation) => {
							if (this.isObject(confirmation)) {
								// order details are present
								const { Orders } = this;
								const { order, blurb } = confirmation;

								order.origin = `PURCHASE`;
								Orders.normalize(order);

								cart.blurb = this.isString(blurb) ? blurb : cart.blurb;
								cart.order = order;
								cart.orderId = order.id;
							}

							cart.completed = true;

							App.unblock({ key: `checkOrderComplete` });

							App.loadOverlay({
								type: `OrderComplete`,
								setup: {
									theme: `LIGHT`,
									overlayMode: `modal`,
									style: `ux4`,
								},
								replace: true,
							});

							App.captureInsight({ eventType: `purchase`, purchaseFlow });
						},
					});
				},
			},

			/**
			 * Ping BE to see if the Stripe order has been completed
			 */
			pingForStripeOrderResult: {
				value(options = {}) {
					const { XHR, data } = this;
					const { cart, event } = data;
					const token = this.isString(options.token, 1) ? options.token : cart.token;
					// transaction should already be completed, followup ping for webhook
					const failHandler = (reason) => {
						if (++pingCount < maxPingCount) {
							return this.defer({
								duration,
								then: () => this.XHR.ajax(ajaxOptions),
							});
						} // else resolve undefined
					};
					const ajaxHandler = (confirmation) => {
						if (this.isObject(confirmation) && this.isObject(confirmation.order)) {
							return confirmation;
						} else {
							return failHandler();
						}
					};
					const ajaxOptions = {
						REST: true,
						type: `GET`,
						url: `/api/events/${event.id}/shoppingcarts/${token}/orders`,
						then: ajaxHandler,
						fail: failHandler,
					};
					const duration = 2000;
					let maxPingCount = 4;
					let pingCount = 0;

					return this.defer({
						duration,
						then: () => XHR.ajax(ajaxOptions), // return the status ping flow for Stripe confirmation
					});
				},
			},

			/**
			 * Update the Stripe intent with the save payment method option
			 * @param {Object} options.savePaymentMethod - Whether to save the payment method
			 */
			updateStripeIntentWithSavePaymentMethod: {
				public: true,
				async: true,
				value(context, options) {
					const { XHR, data } = this;
					const { cart, event } = data;
					const { savePaymentMethod } = options;
					const url = `/api/events/${event.id}/shoppingcarts/${cart.token}?action=save_pm`;
					const body = { savePaymentMethod };
					const xhrOptions = {
						url,
						REST: true,
						data: body,
						type: `PUT`,
					};

					return this.when({
						promise: XHR.ajax(xhrOptions),
					});
				},
			},

			/**
			 * Update the Stripe intent with the use as default
			 * @param {Object} options.useAsDefault - Whether to set the payment method as default
			 */
			updateStripeIntentWithUseAsDefault: {
				public: true,
				async: true,
				value(context, options) {
					const { XHR, data } = this;
					const { cart, event } = data;
					const { useAsDefault } = options;
					const url = `/api/events/${event.id}/shoppingcarts/${cart.token}?action=use_default`;
					const body = { useAsDefault };
					const xhrOptions = {
						url,
						REST: true,
						data: body,
						type: `PUT`,
					};

					return this.when({
						promise: XHR.ajax(xhrOptions),
					});
				},
			},

			// BRAINTREE FLOW, STRIPE WAITLIST, No Payment, External Payment
			callSubmitCartAction: {
				asycn: true,
				value(options) {
					const { App, Payment, XHR, data } = this;
					const { cart, event } = data;
					const { requirements } = cart;
					const { paymentProcessorType } = event;
					const key = `buy`;
					let { tenderType } = options;

					const url = `/api/events/${event.id}/shoppingcarts/${cart.token}`;
					const deviceData = Payment.getDeviceData();
					const payment = { deviceData, tenderType };
					const body = { payment };
					const xhrOptions = {
						url,
						REST: true,
						data: body,
						type: `PUT`,
						then: handler,
						fail: errorHandler,
					};

					if (requirements.q_config) {
						// waitlist order
						xhrOptions.url += `?action=queue`;
					} else {
						// standard order
						xhrOptions.url += `?action=purchase`;
					}

					if (requirements.payment) {
						const nonce = options.nonce;

						payment.save = options.savePaymentMethod;
						payment.useAsDefault = options.useAsDefault;

						if (paymentProcessorType === `STRIPE`) {
							// transaction already completed
							payment.token = options.methodToken; // TODO: can this be removed?
						} else if (this.isString(options.token, 1)) {
							// saved method
							payment.token = options.token;
						} else if (this.isString(nonce, 1)) {
							// manually passed nonce (card or paypal)
							payment.nonce = nonce;

							if (options.tenderType === `CREDIT_CARD`) {
								// BT credit card transaction, pass billing address
								payment.billingAddress = options.billingAddress;
							}
						}
					}

					function handler(response) {
						// successful checkout, reset the cart
						const { confirmation } = response;

						if (!this.isObject(confirmation)) {
							// NOTE: important to return, to avoid running the code below
							if (this.isUndefined(response.cb) || this.isUndefined(response.requestId)) {
								throw new Error(`Invalid cart response received.`);
							} else {
								const duration = response.cb * 1000;

								if (this.isString(response.warning, 1)) {
									App.notifyUser({
										type: `warning`,
										message: response.warning,
										duration,
									});
								}

								return this.defer({
									duration,
									then: () =>
										XHR.ajax({
											url: `/api/requestauthstatus/${response.requestId}`,
											then: handler,
											fail: errorHandler,
										}),
								});
							}
						}

						return confirmation;
					}

					function errorHandler(reason) {
						let docType;
						let json;

						if (this.isString(reason.responseText)) {
							json = JSON.parse(reason.responseText);
							docType = json.docType;
						}

						switch (docType) {
							case `STANDARD_ERROR`:
								switch (json.code) {
									case 2006: // Payment processor issue
										return this.defer({
											duration: 2000 + 1000 * Math.floor(Math.random() * 5),
											then: () => {
												// Random delay has passed
												buyRequestPlaced = false;
												return this.submit();
											},
										}); // returning, no break

									// Error that can not be recovered from, notify the user and destroy the checkout overlay
									case 1002: // Event not found somehow
									case 1003: // Shopping cart not found
									case 1006: // Invalid shopping cart
									case 1007: // Invalid reserve token
									case 2010: // Maximum number of retries attempted
									case 2011: // Processor down; maximum number of retries attempted
										this.init();
								}
								break;

							case `TPS_ERROR`:
								switch (json.code) {
									case 8009: // escrow non-existent
										this.init();
								}
								break;

							default: // Unrecognized docType
						}

						throw reason; // re-throw for the caller to detect
					}

					return this.when({
						promise: XHR.ajax(xhrOptions),
						then: (confirmation) => {
							this.onOrderComplete({ confirmation });
						},
						fin: () => {
							buyRequestPlaced = false;
							App.unblock({ key });
						},
					});
				},
			},

			onOrderComplete: {
				value(options) {
					const { App, Storage, System, data } = this;
					const { cart, purchaseFlow, user } = data;
					const { confirmation } = options;

					if (this.isObject(confirmation)) {
						// order details are present
						const { Orders } = this;
						const { order, blurb } = confirmation;

						order.origin = `PURCHASE`;
						Orders.normalize(order);

						cart.blurb = this.isString(blurb) ? blurb : cart.blurb;
						cart.order = order;
						cart.orderId = order.id;
					}

					// CHECKOUT SUCCESSFUL!
					const { isNativeApp } = System.get();

					cart.completed = true;

					this.setExpiration(null); // clear any timeout behavior
					this.reconcile(); // notify subscribers

					if (isNativeApp && data.isUpgrades) {
						App.callExternal({
							name: `onUpgradeCompleted`,
							data: {
								orderId: cart.orderId,
								email: user.email,
							},
						});
					} else {
						App.loadOverlay({
							type: `OrderComplete`,
							setup: { theme: `LIGHT`, overlayMode: `modal`, style: `ux4` },
							replace: true,
						});
					}

					App.captureInsight({ eventType: `purchase`, purchaseFlow });

					// remove the token and expiration from local storage
					Storage.remove({ key: `cart_token` });
					Storage.remove({ key: `cart_expiration` });
				},
			},

			disablePaymentPlan: {
				async: true,
				public: true,
				value(context) {
					const { App, data } = this;
					const { cart } = data;

					if (cart.usePaymentPlan === true) {
						return this.XHR.ajax({
							type: `PUT`,
							REST: true,
							url: `/api/events/${data.event.id}/shoppingcarts/${cart.token}?action=apply-payment-plan`,
							data: {
								usePaymentPlan: false,
							},
							then: (response) => {
								// success
								cart.usePaymentPlan = false;
								const paymentPlanFee = this.isObject(cart) && this.isInt(cart.paymentPlanFee) ? cart.paymentPlanFee : 0;

								// fire the "cart-remove" event for payment plan
								App.captureInsight({
									eventType: `cart-remove`,
									purchaseFlowItem: `Payment Plan`,
									item: {
										id: `payment_plan`,
										name: `Payment Plan`,
										price: paymentPlanFee,
									},
									productList: {},
								});

								return this.syncCart({
									response,
									then: this.reconcile,
								});
							},
						});
					}
				},
			},

			/**
			 * NOTE: It is NOT safe to assume the user has seats selected when the Promise resolves.
			 * This XHR success implies that Tixr BE talked to seats.io successfully, but the seat
			 * assignment happens when seats.io communicates to the client Floor Plan widget.
			 */
			getBestAvailable: {
				public: true,
				async: true,
				defaults: {},
				async value(context, options) {
					const { App, ReservedSeating, data } = this;
					const { cart, event, isUpgrades } = data;
					const { collectionId, quantity } = options;
					const { groupSaleToken, privateLinkToken, products } = cart;
					const allSales = this.filter(products, { productType: `SALE` });
					const allHolds = this.filter(products, { productType: `HOLD` });
					const accessCodes = this.getAccessCodes();
					const items = [];
					const updates = []; // used for resetting cart quantities back to zero

					if (this.isInt(quantity, 1) && this.isInt(collectionId, 1)) {
						// single quantity, "best available" across a collection
						const { collectionConfiguration } = event;
						const collection = this.find(collectionConfiguration.collections, { id: collectionId });
						const vbas = true; // "validate best available selections" request parameter
						const item = { quantity, vbas };
						const excludedHoldIds = [];
						const saleIds = [];
						const holdIds = [];

						this.each(collection.sales, ({ id }) => {
							const sale = this.find(allSales, { id });
							const { localState, maxTicketsAllowed } = sale;
							let includeSale = localState === `OPEN`;

							if (this.isInt(maxTicketsAllowed, 0)) {
								const { purchasedQuantity } = sale;
								const allowedQuantity = maxTicketsAllowed - purchasedQuantity;

								if (quantity > allowedQuantity) {
									includeSale = false;
								}
							}

							if (includeSale) {
								saleIds.push(id);

								this.each(allHolds, ({ id: holdId, localState: holdLocalState, saleAssignments }) => {
									if (!holdLocalState !== `OPEN`) {
										return;
									}

									const saleAssignment = this.find(saleAssignments, { saleId: id });
									if (this.isObject(saleAssignment)) {
										// hold matches this assignment
										holdIds.push(holdId); // include in the search
									}
								});
							} else {
								// exclude sale and any assigned holds
								const assignedHolds = this.filter(allHolds, (saleAssignments) => {
									const saleAssignment = this.find(saleAssignments, { saleId: id });
									return this.isObject(saleAssignment);
								});
								this.each(assignedHolds, ({ id: holdId }) => excludedHoldIds.push(holdId));
							}
						});

						item.saleIds = saleIds.length > 0 ? saleIds : null;
						// remove any holdIds that were associated with excluded sales
						this.extract(holdIds, (holdId) => this.includes(excludedHoldIds, holdId));
						item.holdIds = holdIds.length > 0 ? holdIds : null;

						// not possible to use priceLevelOptions across a whole collection
						item.priceLevelOptionIds = null;

						items.push(item);
					} else if (this.isInt(quantity, 1) && this.isString(groupSaleToken, 1)) {
						const vbas = true; // "validate best available selections" request parameter
						const holdIds = this.map(allHolds, (hold) => hold.id);
						const saleIds = [];

						items.push({ holdIds, quantity, saleIds, vbas });
					} else {
						// specific quantity / quntities selected, map into multiple asks
						const vbas = false; // "validate best available selections"

						this.each(products, (product) => {
							if (product.quantity > 0) {
								// in cart
								if (product.isReserved) {
									// seatLabel selection required
									const { selections: internalSelections } = product;
									const selections = this.clone(internalSelections);
									const fbaSelections = this.filter(selections, { state: `FBA` });
									const { length: fbaQuantity } = fbaSelections;

									if (fbaQuantity > 0) {
										// seats haven't been selected, use
										const { id, productType } = product;
										let priceLevelOptionIds = null;
										let saleIds = null;
										let holdIds = null;

										if (productType === `SALE`) {
											saleIds = [id];
										} else if (productType === `HOLD`) {
											holdIds = [id];
										}

										this.each(fbaSelections, ({ priceLevelOptionId }) => {
											if (this.isInt(priceLevelOptionId)) {
												if (priceLevelOptionIds === null) {
													priceLevelOptionIds = [priceLevelOptionId];
												} else {
													priceLevelOptionIds.push(priceLevelOptionId);
												}
											}
										});

										// set up the items array for ReservedSeating
										items.push({ holdIds, priceLevelOptionIds, quantity: fbaQuantity, saleIds, vbas });

										// update the state
										this.each(fbaSelections, (selection) => (selection.state = `PENDING`));

										// publish the updated selection states
										updates.push({ id, productType, selections });
									}
								}
							}
						});
					}

					const mode = isUpgrades ? `UPGRADE` : `PURCHASE`;
					const response = await ReservedSeating.getBestAvailable({
						accessCodes,
						groupSaleToken,
						items,
						mode,
						privateLinkToken,
					});
					const { nextToEachOther } = response;

					if (nextToEachOther === false) {
						// property is present and specifically false
						if (event.consecutiveSeatSetting === `LENIENT`) {
							data.ignoreConsecutiveSeatingViolations = true;

							App.loadOverlay({
								type: `Message`,
								setup: {
									message: `Due to high demand, the seats selected are not all together. Please review your cart before completing your purchase to ensure you are satisfied with the seat locations.<br /><br />Thank you for your understanding and enjoy the event!`,
									type: `message`,
									overlayMode: `modal`,
									style: `ux4`,
								},
							});
						}
					}

					if (this.isArray(updates, 1)) {
						this.updateProducts({ products: updates });
					}
				},
			},

			getAccessCodes: {
				public: true,
				value() {
					const { cart } = this.data;
					const { unlockedHolds, saleUnlocks } = cart;

					return this.chain(unlockedHolds).concat(saleUnlocks).map(`accessCode`).filter(this.isString).unique().value();
				},
			},

			makeProductSelection: {
				value(target) {
					const { Utils } = this;
					const uniqueId = Utils.uniqueId();
					const id = parseInt(uniqueId, 10);

					this.assign(target, { id });

					return this.defaults(target, {
						holdId: null,
						holdName: null,
						priceLevelOptionId: null,
						priceLevelOptionName: null,
						row: null,
						saleId: null,
						seat: null,
						seatLabel: null,
						section: null,
						state: `ACTIVE`, // FBA | PENDING | ACTIVE | REMOVING
						tierId: null,
					});
				},
			},

			onDie: {
				public: true,
				value(context) {
					this.unsubscribe({ owner: context });
				},
			},

			navigateToOriginalGroupSale: {
				public: true,
				value(context, options = {}) {
					const { data, Config } = this;
					const { PRIMARY_DOMAIN } = Config;
					const { event, groupSaleAccessCode } = data;
					const { group } = event;
					const { id: groupId } = group;
					const { groupSaleId, groupSaleUrlParam } = options;
					let param = this.isString(groupSaleUrlParam, 1) ? groupSaleUrlParam : data.groupSaleUrlParam;
					const groupSaleIdParam = this.isString(groupSaleId, 1) ? groupSaleId : data.groupSaleId;

					param = `${PRIMARY_DOMAIN}${param}?groupId=${groupId}&gsId=${groupSaleIdParam}`;
					if (this.isString(groupSaleAccessCode, 1)) {
						param += `&ac=${groupSaleAccessCode}`;
					}
					window.location.href = param;
				},
			},

			/**
			 * Upgrades
			 */

			/**
			 * Creates the upgrades object based on the selected source Ids
			 * @param {Object} options - orderId and the sourceSaleOrderItemIds
			 */
			selectUpgradeSources: {
				public: true,
				async value(context, { orderId, sourceSaleOrderItemIds }) {
					const { data, Orders } = this;
					const { cart, user } = data;

					const upgrades = await Orders.getUpgradesByOrderId({ userId: user.id, orderId }); // Todo - cache this api call in Orders.js?
					const order = await Orders.getFanOwnedItemById({ id: orderId }); // Yeah, it would be cool to have a cache on Tixr like DataModel in studio
					const isChildOrder = this.isObject(order.seasonDetails) && order.seasonDetails.seasonEventType === `CHILD`;

					if (this.isArray(sourceSaleOrderItemIds, 1)) {
						const selectedUpgrades = this.map(sourceSaleOrderItemIds, (id) => {
							const upgradeSelected = this.find(upgrades, (upgrade) => upgrade.sourceItem.saleOrderItemId === id);
							const targetOptions = upgradeSelected.targetItems;

							return {
								source: id,
								sourceItem: upgradeSelected.sourceItem,
								targetOptions,
							};
						});

						if (isChildOrder) {
							this.disableOrderProtection();
						}

						this.assign(cart, {
							orderId,
							upgrades: selectedUpgrades,
						});

						this.reconcile();
					}
				},
			},

			/**
			 * Tells the webview to exit the upgrades flow
			 */
			leaveUpgradesMobileWebview: {
				value() {
					const { data, App, System } = this;
					const { cart } = data;
					const { isNativeApp } = System.get();

					if (isNativeApp && cart.leftUpgradesWebview !== true) {
						cart.leftUpgradesWebview = true;
						App.callExternal({
							name: `onAppExitCompleted`,
						});
					}
				},
			},

			/**
			 * Clears the upgrades object, cart, and chart session
			 */
			clearUpgradeSources: {
				public: true,
				value() {
					const { ReservedSeating, data } = this;
					data.cart.upgrades = null;
					ReservedSeating.endSession(); // calling this now eliminates some UI flashing when going back to source selection from reserved upgrades
					this.reconcile();
				},
			},

			/**
			 * Clears the flag for finding best available for upgrades
			 * This is used to know when to zoom in on the seating chart
			 */
			clearFindingBestForUpgradesFlag: {
				public: true,
				value() {
					this.data.cart.findingBestForUpgrades = false;
				},
			},

			/**
			 * Increments the product selection. Upgrade will be added through the sync process
			 */
			incrementUpgrade: {
				public: true,
				defaults: {
					id: null,
					priceLevelOptionId: null,
					productType: null,
					// addOn: null,
					sourceId: null,
				},
				value(context, options) {
					const { data } = this;
					const { id, priceLevelOptionId, productType } = options;
					const { cart } = data;

					const { products } = cart;
					const product = this.find(products, { id, productType });
					const { selections: productSelections } = product;
					const selections = this.clone(productSelections);
					const saleId = id; //productType === `SALE` ? id : options.saleId; // Todo - this is not needed, but may be needed in the future for holds
					const selection = { priceLevelOptionId, saleId };

					selections.push(selection);

					this.updateProducts({
						products: [{ id, productType, selections }],
					});
				},
			},

			/**
			 * Finds the next upgrade that needs a selection
			 */
			getNextUpgradeWithoutSelection: {
				// remove this for the one below or vice versa
				public: true,
				value(context) {
					const { cart } = this.data;
					const { upgrades } = cart;

					const index = this.getNextTargetIndex();

					if (index === -1) {
						return null;
					}

					return upgrades[index];
				},
			},

			/**
			 * Finds the index of the next upgrade that needs a selection
			 */
			getNextTargetIndex: {
				public: true,
				value(context) {
					const { upgrades } = this.data.cart;
					return upgrades.findIndex((upgrade, index) => {
						if (upgrade.releasingSeats) {
							return false;
						}

						if (upgrade.isReserved) {
							return !this.isString(upgrade.selection?.seatLabel, 1);
						}

						return !this.isObject(upgrade.targetSelected);
					});
				},
			},

			isUpgradesReleasingSeats: {
				value() {
					const { upgrades } = this.data.cart;
					const upgradeReleasingSeats = this.find(upgrades, ({ releasingSeats }) => releasingSeats === true);

					return this.isObject(upgradeReleasingSeats);
				},
			},

			/**
			 * Removes the upgrade selection and updates the products
			 */
			deselectUpgradeForSource: {
				public: true,
				value(context, sourceSaleOrderItemId) {
					const { cart } = this.data;
					const { upgrades } = cart;

					const upgrade = this.find(upgrades, (upgrade) => {
						return upgrade.source === sourceSaleOrderItemId;
					});

					if (this.isObject(upgrade?.selection)) {
						const product = this.find(cart.products, (product) => {
							return this.find(product.selections, (selection) => {
								return this.isEqual(selection, upgrade.selection);
							});
						});

						if (this.isObject(product)) {
							const { id, productType } = product;
							let selections = this.clone(product.selections);

							const selection = this.find(selections, (selection) => {
								return this.isEqual(selection, upgrade.selection);
							});

							if (this.isObject(selection)) {
								selections = this.without(selections, selection);
							}

							this.updateProducts({
								products: [{ id, productType, selections }],
							});
						}
					}

					upgrade.selection = null;
					upgrade.targetSelected = null;
					upgrade.isReserved = false;
					upgrade.releasingSeats = false;
				},
			},

			/**
			 * Sync the upgrades with the cart
			 */
			syncUpgradesWithSeatSelections: {
				value() {
					const { ReservedSeating } = this;
					const reservedSeating = ReservedSeating.get();
					const { seatingChartHoldId } = reservedSeating;
					let recalculate = false;

					if (this.checkRemovedUpgrades()) {
						// check to make sure I need to return
						return true; // recalculate
					}

					recalculate = this.findUpgradeForSeatSelection(); // can possibly set limits per sale by categories

					// findBestAvailableForUpgrade
					if (this.isString(seatingChartHoldId)) {
						const needsToReleaseSeats = this.reconcileUpgradesWithoutSeat();
						recalculate = recalculate || needsToReleaseSeats;
					}

					this.calculateUpgradesCheckoutAmount();

					return recalculate;
				},
			},

			/**
			 *	Calculate the checkout amount for upgrades. This must use price difference instead of price.
			 */
			calculateUpgradesCheckoutAmount: {
				value() {
					const { data, Events } = this;
					const { cart } = data;
					const { currency, upgrades } = cart;

					const subtotal = this.reduce(
						upgrades,
						(subtotal, upgrade) => {
							const { targetSelected } = upgrade;

							if (this.isObject(targetSelected)) {
								return subtotal + targetSelected.priceDifference;
							}

							return subtotal;
						},
						0
					);

					if (subtotal === 0) {
						cart.upgradesCheckoutAmount = `Free`;
					} else {
						cart.upgradesCheckoutAmount = Events.formatPriceDifference({ priceDifference: subtotal, currency });
					}
				},
			},

			/**
			 * Checks to see if any upgrades have been removed from the cart and removes the upgrade as well
			 */
			checkRemovedUpgrades: {
				value() {
					const { data } = this;
					const { products, upgrades } = data.cart;
					let recalculate = false;
					let failedBestAvailable = false;

					const upgradeMissingSeat = this.find(upgrades, (upgrade) => {
						const seatSelectionForUpgrade = upgrade.selection;

						if (!this.isObject(seatSelectionForUpgrade)) {
							return false;
						} else {
							const productFound = this.find(products, (product) => {
								return this.find(product.selections, (selection) => {
									return this.isEqual(selection, seatSelectionForUpgrade);
								});
							});

							if (upgrade.failedBestAvailable) {
								failedBestAvailable = true;
								return true;
							}

							return !this.isObject(productFound);
						}
					});

					if (this.isObject(upgradeMissingSeat)) {
						if (failedBestAvailable) {
							this.removeProductSelections({ id: upgradeMissingSeat.selection.id });
						}

						upgradeMissingSeat.selection = null;
						upgradeMissingSeat.targetSelected = null;
						upgradeMissingSeat.failedBestAvailable = false;
						upgradeMissingSeat.isReserved = false;

						recalculate = true;
					}

					return recalculate;
				},
			},

			/**
			 * For any product selection without an upgrade, find the next upgrade and select it
			 */
			findUpgradeForSeatSelection: {
				value() {
					const { data, App } = this;
					const { cart } = data;
					const { products, upgrades } = cart;
					let recalculate = false;
					let seatLabels = [];

					this.each(products, (product) => {
						this.each(product.selections, (selection) => {
							const { saleId, priceLevelOptionId } = selection;
							const { isLevels, isReserved } = product;

							if (selection.state === `REMOVING`) {
								return;
							}

							const existingUpgrade = this.find(upgrades, (upgrade) => {
								return this.isEqual(upgrade.selection, selection);
							});

							if (!this.isObject(existingUpgrade)) {
								let selectionFound = false;

								this.each(upgrades, (upgrade) => {
									if (selectionFound) {
										return;
									}

									if (this.isInt(upgrade.selection?.id) && upgrade.selection.id !== selection.id) {
										return;
									}

									const availableOpenUpgrade = !isReserved && !this.isObject(upgrade.selection);
									const availableReservedUpgrade = isReserved && !this.isString(upgrade.selection?.seatLabel);

									if (availableOpenUpgrade || availableReservedUpgrade) {
										const { targetOptions } = upgrade;
										const targetOption = this.find(targetOptions, (targetOption) => {
											const saleIdMatch = targetOption.saleId === saleId;
											const priceLevelPotentialMatch =
												!isLevels ||
												!this.isInt(priceLevelOptionId) ||
												targetOption.priceLevelOptionId === priceLevelOptionId;
											return saleIdMatch && priceLevelPotentialMatch;
										});

										if (this.isObject(targetOption)) {
											if (isLevels && !this.isInt(priceLevelOptionId)) {
												this.requestMissingPriceLevels();
											}

											upgrade.selection = selection;
											upgrade.targetSelected = targetOption;
											upgrade.isReserved = isReserved;
											selectionFound = true;
											recalculate = true;

											if (this.isString(upgrade.selection?.seatLabel)) {
												upgrade.releasingSeats = false;
											}
										}
									}
								});

								if (!selectionFound) {
									seatLabels.push(selection.seatLabel);
									this.queueSelectionRemoval({
										productId: product.id,
										productType: product.productType,
										selection,
									});
								}
							}
						});
					});

					if (this.isArray(seatLabels, 1)) {
						this.releaseSeats({ seatLabels });

						App.notifyUser({
							type: `warning`,
							message: `No remaining upgrades found for selection${seatLabels.length > 1 ? `s` : ``}.`,
						});
					}

					return recalculate;
				},
			},

			/**
			 * Reconciles upgrades without a seat selection. It will release any seats with the same category before finding the best available
			 */
			reconcileUpgradesWithoutSeat: {
				value() {
					const { data } = this;
					const { cart } = data;
					const { products, upgrades } = cart;

					if (data.findingBest || data.releasingSeats) {
						return true; // recalculate
					}

					// find an upgrade without a seat if one exists
					const upgradeWithoutSeatIndex = upgrades.findIndex((upgrade) => {
						if (!this.isObject(upgrade.targetSelected) || upgrade.selection?.state === `PENDING`) {
							return false;
						}

						const { saleId, tierId, priceLevelOptionId, seatLabel } = upgrade.selection;
						const productFound = this.find(products, (product) => {
							if (saleId !== product.id) {
								return false;
							}

							const tier = this.find(product.tiers, (tier) => {
								if (tierId !== tier.id) {
									return false;
								}

								if (priceLevelOptionId != null) {
									const priceLevelOption = this.find(tier.priceLevels, (priceLevel) => {
										return priceLevelOptionId === priceLevel.priceLevelOptionId;
									});

									return this.isObject(priceLevelOption);
								}

								return true;
							});

							return this.isObject(tier);
						});

						const needToRemoveSeat =
							productFound?.isReserved && this.isObject(upgrade.selection) && !this.isString(seatLabel, 1);

						if (needToRemoveSeat) {
							this.each(productFound.selections, (selection) => {
								if (selection.state !== `REMOVING`) {
									selection.state = `FBA`;
								}
							});
						}

						return needToRemoveSeat;
					});
					const upgradeWithoutSeat = upgrades[upgradeWithoutSeatIndex];

					if (this.isObject(upgradeWithoutSeat)) {
						const seatLabelsToRelease = [];
						const { holdId, saleId, tierId } = upgradeWithoutSeat.targetSelected;

						// find seats of the same category to be released
						this.each(upgrades, (upgrade, index) => {
							if (upgradeWithoutSeatIndex !== index) {
								if (this.isObject(upgrade.selection)) {
									const {
										holdId: currentHoldId,
										saleId: currentSaleId,
										tierId: currentTierId,
										seatLabel: currentSeatlabel,
									} = upgrade.selection;
									if (
										holdId == currentHoldId && // == comparison -> holdId can be null or undefined
										saleId === currentSaleId &&
										tierId === currentTierId &&
										this.isString(currentSeatlabel, 1)
									) {
										seatLabelsToRelease.push(currentSeatlabel);
										upgrade.releasingSeats = true;

										upgrade.selection.seatLabel = null;
									}
								}
							}
						});

						this.App.block(upgradesFindBestBlockOptions);

						if (this.isArray(seatLabelsToRelease, 1) && !data.releaseSeatsBeforeGetBestAvailable) {
							upgradeWithoutSeat.selection.seatLabel = null;
							data.releasingSeats = true;
							data.releaseSeatsBeforeGetBestAvailable = true; // avoiding calling releaseSeats more than once
							this.releaseSeats({ seatLabels: seatLabelsToRelease });
							return true; // recalculate
						}

						data.findingBest = true;
						cart.findingBestForUpgrades = true; // cleared by clearFindingBestForUpgradesFlag(). Used to know when to zoom in on the seating chart
						data.releaseSeatsBeforeGetBestAvailable = false;

						this.getBestAvailable({
							then: (response) => {
								data.findingBest = false;

								this.defer({
									duration: 3000,
									then: () => {
										if (
											this.isObject(this.data.cart.upgrades[upgradeWithoutSeatIndex]?.targetSelected) &&
											!this.isString(this.data.cart.upgrades[upgradeWithoutSeatIndex]?.selection?.seatLabel) &&
											!this.data.cart.upgrades[upgradeWithoutSeatIndex].releasingSeats
										) {
											this.data.cart.upgrades[upgradeWithoutSeatIndex].failedBestAvailable = true;
											this.reconcile();
										}
									},
								});
							},
							fail: (reason) => {
								data.findingBest = false;
								upgradeWithoutSeat.failedBestAvailable = true;
								this.reconcile();
							},
						});
					}

					return false;
				},
			},
		},
	});
});

//# sourceMappingURL=ShoppingCart.js.map